/**
 * i18next setup for the Pro SPA. Dutch is the default/fallback locale (the
 * product is Dutch-first); English is kept in sync key-for-key. Resources are
 * inlined (no async backend) so there's no loading flash and no Suspense churn.
 *
 * The detected language is persisted to localStorage under `biedradar.pro.lang`
 * (mirrors the theme key), then falls back to the browser language, then nl.
 * `<LanguageSwitcher>` flips it at runtime via `i18n.changeLanguage`.
 */
import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

export const LANG_STORAGE_KEY = "biedradar.pro.lang";
export const SUPPORTED_LANGS = ["nl", "en"] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];

const en = {
  common: {
    signIn: "Sign in",
    openWorkspace: "Open workspace",
    openTheWorkspace: "Open the workspace",
    signOut: "Sign out",
    signingOut: "Signing out…",
    newDeal: "New deal",
    cancel: "Cancel",
    close: "Close",
    loading: "Loading…",
  },
  blog: {
    backToApp: "Back to Biedradar Pro →",
    indexTitle: "Blog",
    indexSubtitle:
      "Analyses, figures and practical guides on bidding and the Dutch housing market.",
    readMore: "Read more",
    minRead: "min",
    readingTime: "min read",
    breadcrumb: "Breadcrumb",
    related: "Related guides",
    readArticle: "Read article",
  },
  nav: {
    overview: "Overview",
    clients: "Clients",
    properties: "Properties",
    sellers: "Sellers",
    agenda: "Agenda",
    documents: "Documents",
    team: "Team",
    settings: "Settings",
    workspace: "Workspace",
    planWorkspace: "{{plan}} workspace",
    plan_free: "Free",
    plan_starter: "Starter",
    plan_pro: "Pro",
    plan_scale: "Scale",
    trialTitle: "Free trial",
    trialDaysLeft_one: "{{count}} day left",
    trialDaysLeft_other: "{{count}} days left",
    trialExpiredTitle: "Trial ended",
    trialExpiredBody: "Upgrade to keep using everything.",
    trialUpgrade: "Upgrade",
  },
  properties: {
    title: "Properties",
    subtitle: "{{count}} properties across your deals",
    search: "Search address, client, agent…",
    filterStatus: "Deal status",
    statusAll: "All statuses",
    filterViewed: "Viewing",
    viewedAll: "All",
    colProperty: "Property",
    colClient: "Client",
    colStatus: "Deal",
    colViewing: "Viewing",
    colAgent: "Selling agent",
    loading: "Loading…",
    empty: "No properties yet. Add a property & viewing step on a deal.",
    notFound: "This property could not be found.",
    back: "Back to properties",
    badgeViewed: "Viewed",
    badgePlanned: "Planned",
    notViewed: "Not viewed yet",
    noAgent: "No agent linked",
    cardViewing: "Viewing",
    cardAgent: "Selling agent",
    cardClient: "Client",
    cardDeal: "Deal",
    openDeal: "Open deal",
    seeClient: "See client space",
    remove: "Remove property",
    removeConfirm: "Remove the property step for {{address}}?",
  },
  sellers: {
    title: "Selling agents",
    subtitle: "{{count}} agents across your deals",
    search: "Search agent, agency, client…",
    filterAgency: "Agency",
    allAgencies: "All agencies",
    filterLead: "Lead",
    leadAll: "All agents",
    leadOnly: "Lead only",
    colAgent: "Agent",
    colClient: "Client",
    colProperty: "Property",
    colContact: "Contact",
    loading: "Loading…",
    empty: "No selling agents yet. Add one on a deal's property step.",
    notFound: "This agent could not be found.",
    back: "Back to sellers",
    lead: "Lead",
    cardClient: "Client",
    cardProperty: "Property",
    cardDeal: "Deal",
    openDeal: "Open deal",
    remove: "Remove agent",
    removeConfirm: "Remove {{name}} from this deal?",
  },
  agenda: {
    title: "Agenda",
    subtitle: "Every viewing across your deals, in date order.",
    empty: "No viewings scheduled yet. Add viewings on a deal's property step.",
    noDate: "No date",
    bucket: { today: "Today", week: "This week", later: "Later", none: "No date", past: "Past" },
    search: "Search client, address…",
    filterStatus: "Status",
    statusAll: "All statuses",
  },
  cmdk: {
    placeholder: "Search deals, clients, pages…",
    empty: "No results.",
    pages: "Pages",
    deals: "Deals & clients",
    hintNavigate: "navigate",
    hintOpen: "open",
    hintClose: "close",
  },
  view: {
    flow: "Flow",
    board: "Board",
    hintTitle: "Two ways to view",
    hintBody: "See this space as a flow diagram or a Trello-style board. Switch anytime here.",
    hintGotIt: "Got it",
  },
  sign: {
    loading: "Loading…",
    invalid: "This signing link is invalid or has expired.",
    signedTitle: "Signed",
    signedBody: "Thank you, {{name}}. Your signature for “{{title}}” has been recorded.",
    asksYouToSign: "{{org}} has asked you to sign",
    documentLabel: "Document",
    openDocument: "Open document in a new tab",
    yourSignature: "Your signature",
    drawHere: "Draw your signature here",
    clear: "Clear",
    consent: "By signing, {{name}} agrees this electronic signature is legally binding.",
    signButton: "Sign document",
    signing: "Signing…",
    failed: "Signing failed. Check your connection and try again.",
    auditNote: "A timestamped audit record (time, IP) is stored with your signature.",
    poweredBy: "Powered by Biedradar Pro · internal signing tier",
    signedOn: "Signed on {{date}}",
    typeTab: "Type",
    drawTab: "Draw",
    typedPlaceholder: "Type your full name",
    typedPreviewHint: "Your signature appears here",
    download: "Download",
    loadingDocument: "Loading document…",
    previewUnavailable: "This document can’t be previewed here.",
    signHere: "Sign here",
    placedHint: "Your advisor has marked where to sign — see the highlighted box above.",
  },
  documents: {
    title: "Documents",
    subtitle: "{{count}} files across your deals",
    request: "Request",
    requestDoc: "Request a document",
    requestSignature: "Request a signature",
    search: "Search files, clients…",
    filterStatus: "Status",
    filterDeal: "Deal",
    filterKind: "Kind",
    filterSignature: "Signature",
    sort: "Sort",
    statusAll: "All statuses",
    statusRequested: "Requested",
    statusReceived: "Received",
    statusInReview: "In review",
    statusApproved: "Approved",
    dealAll: "All deals",
    kindAll: "All kinds",
    kindFile: "Files",
    kindAwaiting: "Awaiting upload",
    sigAll: "Any signature",
    sigPending: "Awaiting signature",
    sigSigned: "Signed",
    sigBadgePending: "To sign",
    sigBadgeSigned: "Signed",
    sigBadgeExpired: "Expired",
    sortRecent: "Most recent",
    sortName: "Name (A–Z)",
    sortStatus: "Status",
    colFile: "File",
    colDeal: "Deal",
    colStatus: "Status",
    colSignature: "Signature",
    colUploaded: "Uploaded",
    awaitingUpload: "awaiting upload",
    download: "Download",
    loading: "Loading…",
    empty: "No documents match these filters.",
    requestDocTitle: "Request a document",
    requestSignatureTitle: "Request a signature",
    requestDocConfirm: "Send request",
    requestSignatureConfirm: "Create signing link",
    done: "Done",
    fieldDeal: "Deal",
    fieldDealPlaceholder: "Select a deal…",
    fieldDocument: "Document to sign",
    fieldDocumentNone: "No document — acknowledgement only",
    fieldName: "What to request",
    fieldNamePlaceholder: "e.g. Proof of income",
    fieldTitle: "Title",
    fieldTitlePlaceholder: "e.g. Service agreement",
    fieldSigner: "Signer",
    fieldSignerPlaceholder: "Full name",
    signatureLinkReady: "Signing link for {{name}} is ready — share it:",
    copied: "Copied!",
    placement: "Signature placement",
    placeHint: "Click or drag on the document to mark where the signature goes.",
    placeTitle: "Place the signature",
    placeNoDoc: "Pick a document first to place the signature.",
    placementAdd: "Place on document",
    placementEdit: "Move signature box",
    placementClear: "Remove",
    placementSet: "Placed on page {{page}}",
    placementDone: "Use this spot",
    placementSkip: "Skip placement",
  },
  landing: {
    seoLang: "en",
    seoTitle: "Biedradar — smarter bidding on Dutch homes",
    seoDescription:
      "Biedradar turns Kadaster, CBS and PDOK data into a clear bid analysis — comparables, overbid stats, appraisal risk and a mortgage estimate. Pro workspace for advisors and agencies.",
    heroShotZoom: "click to enlarge",
    heroPrev: "Previous screenshot",
    heroNext: "Next screenshot",
    heroShots: {
      dashboard: "Pipeline overview",
      canvas: "Deal canvas",
      dealDetail: "Step details & financing",
      clients: "Clients",
      properties: "Properties",
      sellers: "Selling agents",
      team: "Team & seats",
      command: "Command palette",
      settings: "Workspace settings",
    },
    navFeatures: "Features",
    navBlog: "Blog",
    signIn: "Sign in",
    badge: "For mortgage advisors & makelaars",
    titleLine1: "Run every deal from",
    titleLine2: "offer to keys.",
    subtitle:
      "Biedradar Pro is the deal workspace for Dutch real estate — guide every home purchase from the first bid to the notary, with the financing gap, documents, e-signing and a client portal in one place.",
    ctaPrimary: "Start now free",
    ctaSecondary: "Sign in",
    ctaNote: "7-day free trial · Cancel anytime",
    heroShotAlt: "The Biedradar Pro dashboard",
    trustLabel: "Built on official Dutch sources",
    canvasEyebrow: "The deal canvas",
    canvasTitle: "Every deal as a living flow",
    canvasBody:
      "Drag each step from bid to keys onto one canvas. See what's done, what's blocked and what's next — for every client, at a glance.",
    canvasB1: "Buyer journey, appraisal, mortgage, notary — pre-built",
    canvasB2: "Real-time, so your whole team sees the same board",
    canvasB3: "Flow or Trello-style board — switch anytime",
    canvasShotAlt: "A deal on the Biedradar Pro canvas",
    pipelineEyebrow: "Overview",
    pipelineTitle: "Your whole pipeline at a glance",
    pipelineBody:
      "Pipeline value, the deals that need action and what's been won — without a spreadsheet. The dashboard surfaces the deals that need you today.",
    pipelineB1: "Live pipeline value and own-funds gap",
    pipelineB2: "Blockers and recent activity, front and centre",
    pipelineB3: "Every client and property, searchable",
    pipelineShotAlt: "The Biedradar Pro pipeline",
    portalEyebrow: "Client portal",
    portalTitle: "Bring your client into the deal",
    portalBody:
      "Share a clean, read-only space so buyers follow progress, upload documents and sign — no email threads, no chasing.",
    portalB1: "Read-only space, branded as you",
    portalB2: "Document requests and uploads",
    portalB3: "E-signing built in",
    featuresTitle: "Everything a deal needs",
    featuresSubtitle:
      "From the first bid to the keys — without leaving the workspace.",
    f1Title: "Financing gap",
    f1Body:
      "See the own-funds shortfall on every deal, the moment the numbers change.",
    f2Title: "E-signing",
    f2Body:
      "Send and sign documents in the browser — placed exactly where they belong.",
    f3Title: "Documents",
    f3Body:
      "Request, collect and track every file per step, with status at a glance.",
    f4Title: "Viewings agenda",
    f4Body: "Every viewing across your deals in one schedule.",
    f5Title: "Real-time team",
    f5Body: "Everyone sees the same board, updating live as the deal moves.",
    f6Title: "Deal templates",
    f6Body: "Spin up a new deal from your own pipeline in one click.",
    ctaTitle: "Run every deal like a pro.",
    ctaBody:
      "Open the workspace and walk your next client from offer to keys.",
    ctaButton: "Start now free",
    ctaFootnote: "7-day free trial · Cancel anytime · EU-hosted",
    socialProof: "Trusted by 130+ agents & makelaars",
    marqueeLabel: "Used by agencies and advisors across the Netherlands",
    stat1Value: "€25M+",
    stat1Label: "in real estate guided every year",
    stat2Value: "130+",
    stat2Label: "agents & advisors on board",
    stat3Value: "2,000+",
    stat3Label: "buyers guided to the keys",
    stat4Value: "4.9/5",
    stat4Label: "average advisor rating",
    testimonialsEyebrow: "Loved by agents",
    testimonialsTitle: "Built for the way you actually work",
    testimonialsSubtitle:
      "Mortgage advisors and aankoopmakelaars across the Netherlands run their deals on Biedradar Pro.",
    t1Quote:
      "I used to run deals across email, WhatsApp and three spreadsheets. Now my whole team sees one board — and my clients finally know where they stand.",
    t1Name: "Sanne de Vries",
    t1Role: "Aankoopmakelaar · De Sleutel Aankoop",
    t2Quote:
      "The financing gap on every deal, the moment the numbers change. It's the first thing I check each morning.",
    t2Name: "Thomas Bakker",
    t2Role: "Mortgage advisor · Amstelhaven Hypotheken",
    t3Quote:
      "Sharing a clean client portal instead of endless email threads saves me hours every week. Buyers love it.",
    t3Name: "Yasmin El Amrani",
    t3Role: "Aankoopmakelaar · Polderhuis Makelaardij",
    t4Quote:
      "The financing gap and every what-if scenario live in one place. I walk into each bidding round knowing exactly what my client can carry.",
    t4Name: "Daan Koster",
    t4Role: "Mortgage advisor · Noorderlicht Hypotheken",
    t5Quote:
      "Clients sign the purchase agreement from their phone in minutes. No printing, no scanning, no chasing — it just gets done.",
    t5Name: "Lotte Visser",
    t5Role: "Aankoopmakelaar · Grachtwerk Advies",
    t6Quote:
      "We onboarded eight advisors in a week. Everyone works the same way now, and I can finally see the whole agency's pipeline at a glance.",
    t6Name: "Mehmet Yıldız",
    t6Role: "Agency owner · Stadshuis Makelaars",
    t7Quote:
      "Before, a deal slipping through the cracks meant a lost commission. Now nothing moves without me seeing it. My follow-up rate went through the roof.",
    t7Name: "Iris Hendriks",
    t7Role: "Aankoopmakelaar · Veldhuis & Co",
    t8Quote:
      "The WOZ and overbid context lands in the report automatically. I stopped pulling numbers by hand and got two evenings a week back.",
    t8Name: "Bram Mulder",
    t8Role: "Mortgage advisor · Rivierhof Hypotheken",
    t9Quote:
      "My clients used to call me anxious before every bidding round. Now they open the portal and see exactly where things stand. The calls basically stopped.",
    t9Name: "Fatima Ouali",
    t9Role: "Aankoopmakelaar · Binnenstad Aankoop",
    t10Quote:
      "I closed three deals in a month I'd normally have dropped. The pipeline view simply doesn't let anything go cold.",
    t10Name: "Sven Jansen",
    t10Role: "Aankoopmakelaar · Duinrand Makelaardij",
    t11Quote:
      "We run two offices off one board. I can see who's overloaded and rebalance before a client ever notices a delay.",
    t11Name: "Anouk Smit",
    t11Role: "Agency owner · Westerpoort Groep",
    t12Quote:
      "The financing scenarios are the killer feature. I model three offers live with a client and they decide in minutes, not days.",
    t12Name: "Ravi Pratap",
    t12Role: "Mortgage advisor · Zonnehuis Financieel",
    t13Quote:
      "Signing moved from a week of chasing paperwork to the same afternoon. Buyers are genuinely surprised how fast it goes.",
    t13Name: "Marit Visscher",
    t13Role: "Aankoopmakelaar · Hanze Aankoop",
    t14Quote:
      "I switched from a generic CRM that never understood Dutch deals. Biedradar just speaks the language — WOZ, NHG, overbidding, all of it.",
    t14Name: "Joost van Dijk",
    t14Role: "Mortgage advisor · Maasstad Hypotheken",
    t15Quote:
      "Onboarding a new advisor used to take a month of hand-holding. Now they're productive on day two because the board explains itself.",
    t15Name: "Selin Demir",
    t15Role: "Agency owner · Parkzicht Makelaars",
    t16Quote:
      "I handle twice the deals I did last year with the same hours. It's the first tool that actually scaled with me instead of slowing me down.",
    t16Name: "Pieter de Groot",
    t16Role: "Aankoopmakelaar · Lindelaan Advies",
    t17Quote:
      "The mobile portal is what sold my clients. They sign on the train, upload documents from the kitchen table. No friction at all.",
    t17Name: "Nadia Bakir",
    t17Role: "Mortgage advisor · Havenkwartier Hypotheken",
    t18Quote:
      "I trust the appraisal-risk flag more than my own gut now. It caught two deals heading for a financing problem before they blew up.",
    t18Name: "Erik Schouten",
    t18Role: "Aankoopmakelaar · Vesting Makelaardij",
    faqEyebrow: "FAQ",
    faqTitle: "Questions, answered",
    q1: "Who is Biedradar Pro for?",
    a1: "Dutch mortgage advisors and aankoopmakelaars who guide buyers from the first bid to the notary.",
    q2: "Does it work with my whole team?",
    a2: "Yes — every deal is a shared, real-time board, so your team always sees the same status, live.",
    q3: "Can my client follow along?",
    a3: "Share a clean, read-only client portal where buyers track progress, upload documents and sign — branded as you.",
    q4: "Where is my data stored?",
    a4: "On EU-hosted infrastructure in Frankfurt, GDPR-compliant. Your clients' data stays in the EEA.",
    q5: "What does it cost?",
    a5: "Plans start at €49/month and include 5 seats, with a 7-day free trial and no lock-in. See the pricing page for the full breakdown.",
    q6: "How does pricing work?",
    a6: "Plans start at €49/month and include 5 seats. Need more people? Extra seats are €5/seat/month. Pick the tier that matches your client volume.",
    q7: "Is there a free trial?",
    a7: "Yes — every plan starts with a 7-day free trial, no credit card required. Add a payment method whenever you're ready via Settings → Billing; until you do, you simply won't be charged anything.",
    q8: "Can I cancel anytime?",
    a8: "Yes. There's no lock-in — cancel from your billing settings whenever you like and you won't be billed again.",
    q9: "Is the e-signing legally valid?",
    a9: "Yes. Every signature is captured with a full audit trail (timestamp, IP, document hash) — a valid electronic signature under eIDAS for purchase agreements.",
    q10: "Can clients upload documents securely?",
    a10: "Buyers upload payslips, ID and bank statements straight into their private portal — encrypted in transit and at rest, never over email.",
    q11: "Where does the property data come from?",
    a11: "The same official Dutch sources as Biedradar: Kadaster, CBS, PDOK, EP-Online and Politie. Comparable sales, WOZ and neighbourhood data sit inside each deal.",
    q12: "How do I get started?",
    a12: "Pick a plan to start your 7-day free trial (no credit card), invite your team and create your first deal — it takes minutes. Import existing clients as you go; there's nothing to install.",
    priceSeoTitle: "Pricing",
    priceSeoDesc:
      "Transparent per-agency pricing for Biedradar Pro. Plans from €49/month with 5 seats included, a 7-day free trial and no lock-in.",
    priceEyebrow: "Pricing",
    priceTitle: "One workspace for your whole agency",
    priceSubtitle:
      "Every plan includes 5 seats, the full Pro app and a 7-day free trial. Scale up only when you grow.",
    priceTrial: "7-day free trial",
    priceNoCard: "No charge for 7 days",
    priceCancel: "Cancel anytime",
    priceEu: "EU-hosted · GDPR/AVG",
    pricePerMonth: "/month",
    priceSeatsIncluded: "5 seats included",
    pricePerSeat: "then €5 / extra seat / month",
    priceFeatured: "Most chosen",
    priceCta: "Start free trial",
    priceStarterTagline: "For solo advisors & small teams",
    priceProTagline: "For growing agencies",
    priceScaleTagline: "For high-volume brokerages",
    priceRowActiveClients: "Active clients",
    priceRowNewClients: "New clients / month",
    priceRowUploads: "Document uploads / month",
    priceRowSigning: "E-sign requests / month",
    priceUnlimited: "Unlimited",
    priceSeatNote: "All plans include 5 seats. Add teammates for €5 per seat per month.",
    priceIncludedTitle: "In every plan",
    priceIncl1: "Real-time deal boards for your whole team",
    priceIncl2: "Branded client portal with document upload",
    priceIncl3: "Legally-valid e-signing (eIDAS)",
    priceIncl4: "Kadaster, CBS & PDOK data inside every deal",
    priceIncl5: "EU-hosted, encrypted, GDPR-compliant",
    priceIncl6: "Unlimited deals & properties",
    priceGuaranteeTitle: "7-day free trial, cancel anytime",
    priceGuaranteeBody:
      "Try the full Pro app free for 7 days — no credit card required. Upgrade from billing whenever you're ready; if you don't, you're never charged. No lock-in, no cancellation fee.",
    priceFaqTitle: "Pricing questions?",
    priceFaqBody: "Read the FAQ or email us — we'll help you pick the right plan.",
    priceFaqCta: "Read the FAQ",
    priceClosingTitle: "Start your 7-day free trial today",
    priceClosingBody: "Set up your agency, invite your team and run your first deal in minutes.",
    priceClosingCta: "Start now free",
    footerProduct: "Product",
    footerResources: "Resources",
    footerLegal: "Legal",
    linkFeatures: "Features",
    linkPricing: "Pricing",
    linkCalculator: "Calculator",
    linkContact: "Contact",
    linkGuides: "Guides",
    linkPrivacy: "Privacy",
    linkTerms: "Terms",
    linkGdpr: "GDPR / AVG",
    linkDisclaimer: "Disclaimer",
    linkEncryption: "Encryption",
    linkDutchData: "Data in the EU",
    securityEyebrow: "Security & privacy",
    securityTitle: "Your clients' data, encrypted and EU-only",
    securityBody:
      "Biedradar Pro handles sensitive financial and personal data — secured end to end and kept inside the European Union.",
    sec1Title: "Encrypted in transit & at rest",
    sec1Body:
      "Every connection is TLS-encrypted and data is encrypted at rest — from the first bid to the signed deed.",
    sec2Title: "EU-hosted, AVG-proof",
    sec2Body:
      "Servers and backups live in Frankfurt; your clients' data never leaves the EEA. GDPR/AVG by design.",
    sec3Title: "Role-based access",
    sec3Body:
      "Granular team roles, and clients only ever see their own deal — in a read-only portal.",
    sec4Title: "You stay in control",
    sec4Body: "Export or delete your data whenever you want. It's yours, not ours.",
    securityPoliciesLabel: "Read the details",
    momentEyebrow: "The keys moment",
    momentTitle: "Every deal ends with someone moving in.",
    momentBody:
      "Behind every bid is a person waiting for the keys. Biedradar Pro keeps the whole journey clear — for you, your team and your client — so nothing slips between the first offer and move-in day.",
    momentPhoto1Alt: "A family moving into their new Dutch home",
    momentPhoto2Alt: "A home buyer reviewing their mortgage at a laptop",
    momentPhoto3Alt: "A happy couple after buying their home",
    linkSecurity: "Security",
    linkCookies: "Cookies",
    baEyebrow: "Before / After",
    baTitle: "From scattered chaos to one clear workspace",
    baSubtitle:
      "Advisors who switch to Biedradar stop juggling inboxes and spreadsheets — and start winning more deals, faster.",
    baBeforeKicker: "Without Biedradar",
    baBeforeLabel: "Guesswork & lost overview",
    baAfterKicker: "With Biedradar",
    baAfterLabel: "Every deal under control",
    baBefore1: "Deals scattered across inbox, WhatsApp and spreadsheets",
    baBefore2: "Bids guessed without comparable sales or overbid data",
    baBefore3: "Clients wait, deadlines slip, value walks out the door",
    baAfter1: "Every deal, client and document in one workspace",
    baAfter2: "Data-backed bids — comparables, overbid stats, appraisal risk",
    baAfter3: "More clients handled, nothing dropped, deals closed faster",
    baImgBeforeAlt: "An unreadable, scattered workflow before Biedradar",
    baImgAfterAlt: "The Biedradar dashboard: €2M+ pipeline across dozens of active clients",
    baAfterBadge1: "€3M+ pipeline",
    baAfterBadge2: "27 active deals",
    baBeforeStat1V: "€800K",
    baBeforeStat1L: "real estate / month",
    baBeforeStat2V: "1 in 3",
    baBeforeStat2L: "bids lost on price",
    baBeforeStat3V: "10 hrs/wk",
    baBeforeStat3L: "lost to busywork",
    baAfterStat1V: "€2M+",
    baAfterStat1L: "real estate / month",
    baAfterStat2V: "60%",
    baAfterStat2L: "more bids won",
    baAfterStat3V: "5×",
    baAfterStat3L: "more clients / month",
    galleryEyebrow: "A closer look",
    galleryTitle: "Built for the depth of a real deal",
    gallerySubtitle: "Not a demo — the actual workspace your team works in every day.",
    galCap1: "Every step, with financing and documents",
    galCap2: "Jump anywhere with ⌘K",
    galCap3: "Every property across your deals",
    galCap4: "Your branding, team and billing",
    footerTagline:
      "The deal-flow workspace for Dutch mortgage advisors and estate agencies.",
    footerMadeInNl: "Made in NL",
    footerRights: "All rights reserved.",
  },
  legal: {
    backHome: "Back to Biedradar Pro",
    lastUpdated: "Last updated",
    morePolicies: "More policies",
  },
  topbar: {
    searchDeals: "Search deals…",
  },
  empty: {
    goToDeals: "Go to deals",
    dashTitle: "Welcome to your workspace",
    dashBody: "Create your first deal and you'll see your pipeline, financing gaps and recent activity here.",
    clientsTitle: "No deals yet",
    clientsBody: "Spin up your first deal — pick a pipeline, add the client and the property, and start working it.",
    propertiesTitle: "No properties yet",
    propertiesBody: "Properties appear here as you add property & viewing steps to your deals.",
    sellersTitle: "No selling agents yet",
    sellersBody: "Selling agents you add on a deal's property step show up here, across every deal.",
    agendaTitle: "Nothing scheduled",
    agendaBody: "Viewings appear here once you set a date on a deal's property step.",
    documentsTitle: "No documents yet",
    documentsBody: "Request a file or a signature on a deal, or upload one — they'll all be listed here.",
    teamTitle: "It's just you",
    teamBody: "Invite colleagues so you can work deals together. Admins manage the team and billing.",
  },
  profile: { account: "Account", settings: "Settings" },
  viewings: {
    header: "Viewings",
    add: "Add viewing",
    addPlaceholder: "Address of a viewed property…",
    agent: "Selling agent",
    agentNone: "No agent",
    notes: "Notes",
    remove: "Remove viewing",
    status: { scheduled: "Scheduled", viewed: "Viewed", interested: "Interested", rejected: "Rejected" },
  },
  viewing: {
    header: "Viewing",
    viewedQuestion: "Viewed?",
    yes: "Yes",
    no: "No",
    date: "Date",
    statusViewed: "Viewed",
    statusPlanned: "Planned",
  },
  agents: {
    header: "Selling agents",
    buyer: "Buyer",
    empty: "No selling agents yet.",
    primary: "Lead",
    makePrimary: "Set as lead agent",
    remove: "Remove agent",
    add: "Add agent",
    addTitle: "Add selling agent",
    adding: "Adding…",
    addConfirm: "Add agent",
    name: "Contact name",
    agency: "Agency",
    email: "Email",
    phone: "Phone",
  },
  clientCard: {
    open: "Open client card",
    title: "Client",
    subtitle: "Contact, financial position & this deal",
    name: "Name",
    email: "Email",
    phone: "Phone",
    notes: "Notes",
    notesPlaceholder: "Anything worth remembering about this client…",
    financialsSummary: "Financial position",
    editFinancials: "Edit financials",
    income: "Gross income",
    ownFunds: "Own funds",
    gap: "Funding gap",
    dealSection: "This deal",
    save: "Save",
    saving: "Saving…",
  },
  join: {
    title: "Join the workspace",
    subtitle: "Accept your invite to start collaborating on deals.",
    authSubtitle: "Create your account or sign in to accept the invite — you'll join the workspace right away.",
    confirmSubtitle: "You're signed in as {{email}}. Accept the invite to join the workspace.",
    nameLabel: "Your name",
    emailLabel: "Email",
    passwordLabel: "Password",
    emailHint: "Use the email address your invite was sent to.",
    createAndJoin: "Create account & join",
    signInAndJoin: "Sign in & join",
    haveAccount: "Already have an account? Sign in",
    noAccount: "New here? Create an account",
    accept: "Accept invite",
    joining: "Joining…",
    emailMismatch: "This invite is for a different email. Use the invited address.",
    error: "This invite is invalid or has expired.",
    toWorkspace: "Go to workspace",
  },
  public: {
    openApp: "Open app",
    footer: { home: "Home", status: "Status", contact: "Contact", privacy: "Privacy" },
  },
  contact: {
    eyebrow: "Contact",
    title: "Get in touch",
    subtitle: "Questions about Biedradar, your account or a partnership? Send us a message and we'll reply by email.",
    nameLabel: "Name",
    emailLabel: "Email",
    subjectLabel: "Subject",
    subjectPlaceholder: "What's it about?",
    messageLabel: "Message",
    messagePlaceholder: "How can we help?",
    send: "Send message",
    sending: "Sending…",
    error: "Couldn't send your message — please try again, or email us directly.",
    successTitle: "Message sent",
    successBody: "Thanks for reaching out. We'll get back to you by email shortly.",
    privacyNote: "We only use your details to reply.",
  },
  statusPage: {
    title: "Service status",
    subtitle: "Live health of Biedradar's systems. This page needs no account.",
    checking: "Checking…",
    refresh: "Refresh",
    updatedAt: "Updated at {{time}}",
    footnote: "Status refreshes automatically every 30 seconds.",
    overall: {
      operational: "All systems operational",
      degraded: "Some systems degraded",
      down: "Major outage",
    },
    componentStatus: {
      operational: "Operational",
      degraded: "Degraded",
      down: "Down",
    },
    components: {
      api: "API",
      database: "Database",
      cache: "Cache",
      email: "Email delivery",
    },
    componentsHint: {
      api: "Report generation & app backend",
      database: "Deals, clients & documents",
      cache: "Rate limiting & response caching",
      email: "Invites, sign requests & notifications",
    },
  },
  customTemplate: {
    new: "New template",
    title: "New template",
    subtitle: "Save a reusable pipeline. New deals can start from it instead of the defaults.",
    name: "Template name",
    namePlaceholder: "e.g. New-build purchase",
    steps: "Steps",
    addStep: "Add step",
    emptySteps: "Add at least one step.",
    save: "Save template",
    saving: "Saving…",
    moveUp: "Move up",
    moveDown: "Move down",
    removeStep: "Remove step",
    delete: "Delete template",
    stepCount_one: "{{count}} step",
    stepCount_other: "{{count}} steps",
  },
  newDeal: {
    title: "New deal",
    subtitle: "Pick a pipeline, then add the client and property — the deal opens ready on the canvas.",
    sectionTemplate: "Pipeline template",
    sectionClient: "Client",
    clientName: "Client name",
    clientEmail: "Email",
    clientPhone: "Phone",
    sectionProperty: "Property",
    address: "Address",
    addressPlaceholder: "Start typing an address…",
    postcode: "Postcode",
    askingPrice: "Asking price",
    estimatedValue: "Estimated value",
    sectionAssignment: "Assignment",
    assignee: "Assigned advisor",
    unassigned: "Unassigned",
    create: "Create deal",
    creating: "Creating…",
    limitTitle: "You've reached your plan's client limit.",
    limitBody: "Upgrade to add more clients this month.",
    viewPlans: "View plans",
    error: "Couldn't create the deal. Please try again.",
  },
  templates: {
    buy: { name: "Purchase", description: "The standard buyer journey: from viewing to keys." },
    mortgage: { name: "Mortgage advice", description: "Advisory track: intake, application, appraisal, closing." },
    sell: { name: "Sale", description: "Listing track: from intake to transfer at the notary." },
    refinance: { name: "Refinance", description: "Switch an existing mortgage to better terms." },
  },
  dealSettings: {
    title: "Deal settings",
    name: "Deal name",
    description: "Description",
    descriptionPlaceholder: "Notes about this deal…",
    address: "Address",
    addressPlaceholder: "Street, number, city",
    status: "Status",
  },
  stepInfo: {
    awaitingOffer: "Awaiting offer",
    transferAtNotary: "Transfer at notary",
    eSignature: "E-signature",
    signed: "Signed",
    signatureCount_one: "{{count}} awaiting signature",
    signatureCount_other: "{{count}} awaiting signature",
    documentsCount_one: "{{count}} document",
    documentsCount_other: "{{count}} documents",
    documentsRequested: "Documents requested",
    financialProfile: "Financial profile",
  },
  deal: {
    backToClients: "Back to clients",
    loading: "Loading deal…",
    notFound: "This deal could not be found.",
    shareSpace: "Share space",
    copySpaceLink: "Copy space link",
    linkCopied: "Link copied",
    creating: "Creating…",
    fullyFinanced: "Fully financed",
    viewing: "{{count}} viewing: {{names}}",
    advisorFallback: "Advisor",
    commentsButton: "Discussion",
    commentsTitle: "Deal discussion",
    commentsEmpty: "No comments yet. Start the deal discussion.",
  },
  financing: {
    openButton: "Financials",
    title: "Financing",
    subtitle: "{{client}}'s position and the mortgage for this deal.",
    clientSection: "Client — income & own funds",
    grossIncome: "Gross annual income",
    partnerIncome: "Partner gross income",
    ownFunds: "Own funds (savings)",
    otherMonthlyIncome: "Other income (monthly)",
    monthlyObligations: "Monthly obligations",
    studentDebt: "Student debt",
    mortgageSection: "Mortgage",
    bank: "Bank",
    bankPlaceholder: "e.g. ABN AMRO, Rabobank",
    maxMortgage: "Max mortgage",
    interestRate: "Interest rate (%)",
    termYears: "Term (years)",
    mortgageType: "Repayment type",
    nhg: "NHG (mortgage guarantee)",
    type: { annuity: "Annuity", linear: "Linear", interest_only: "Interest-only" },
    gapSection: "Funding gap",
    purchasePrice: "Purchase price",
    appliedMax: "Mortgage (capped)",
    ownFundsApplied: "Own funds",
    gap: "Own-funds gap",
    fullyFinanced: "Fully financed",
    save: "Save financing",
    saving: "Saving…",
  },
  settings: {
    title: "Settings",
    subtitle: "Manage your organization, team and account.",
    orgTitle: "Organization settings",
    orgSubtitle: "Your workspace identity, branding and team.",
    nav: { organization: "Organization", members: "Members", billing: "Billing", account: "Account" },
    org: {
      title: "Organization",
      nameLabel: "Organization name",
      plan: "Plan",
      accent: "Accent color",
      accentHint: "Used on the client-facing portal and shared reports.",
      accentInvalid: "Use a hex color like #0f7652.",
      save: "Save changes",
      saving: "Saving…",
      saved: "Saved",
      saveFailed: "Saving failed — check the fields and try again.",
      memberCount_one: "{{count}} member in this workspace",
      memberCount_other: "{{count}} members in this workspace",
      brandingTitle: "Branding",
      brandingSubtitle: "Shown on the client portal and shared reports.",
      logoLabel: "Logo URL",
      logoHint: "Direct link to a PNG or SVG. Leave empty to use the Biedradar wordmark.",
      logoInvalid: "Enter a valid http(s) URL, or leave it empty.",
      logoPreviewAlt: "Organization logo",
      teamTitle: "Team",
      teamSubtitle: "Who's in this workspace, by role.",
      roleAdmins: "Admins",
      roleAdvisors: "Advisors",
      roleAssistants: "Assistants",
      roleClients: "Portal clients",
      manageTeam: "Manage members",
      businessTitle: "Business details",
      businessSubtitle: "Used on invoices, shared reports and the client portal.",
      portalTitle: "Client portal",
      portalSubtitle: "What clients see when they open their deal space.",
      profile: {
        contactEmail: "Contact email",
        phone: "Phone",
        website: "Website",
        kvk: "Chamber of Commerce (KvK)",
        vat: "VAT number",
        addressLine: "Address",
        postalCode: "Postal code",
        city: "City",
        supportEmail: "Support email",
        portalWelcome: "Portal welcome message",
        portalWelcomePlaceholder: "A short greeting shown to clients in their space…",
      },
      detailsTitle: "Details",
      orgId: "Organization ID",
      copyId: "Copy ID",
      created: "Created",
      dangerTitle: "Danger zone",
      dangerBody: "Deleting an organization is permanent — it removes every deal, document and member. Reach out and we'll handle it with you.",
      dangerContact: "Contact support",
    },
    members: {
      title: "Members & roles",
      subtitle: "Admins invite and manage the team. Members work deals; view-only clients see the portal.",
      you: "you",
      remove: "Remove member",
      removeConfirm: "Remove {{name}} from this workspace?",
      rolesHint: "Admin — manage & invite · Member — work deals · Client — view-only.",
      inviteTitle: "Invite a teammate",
      invitePlaceholder: "teammate@agency.nl",
      inviteSend: "Send invite",
      inviteSending: "Sending…",
      inviteLinkLabel: "Invite link (also emailed) — share it to let them join:",
      copied: "Copied!",
      pendingTitle: "Pending invites",
      pendingEmpty: "No pending invites.",
      revoke: "Revoke invite",
    },
    account: {
      title: "Account",
      subtitle: "Your personal profile, preferences and security.",
      profileTitle: "Profile",
      preferencesTitle: "Preferences",
      language: "Language",
      theme: "Theme",
      securityTitle: "Security",
      demoNote: "You're viewing the demo workspace. Sign in to manage your own account.",
      passwordTitle: "Change password",
      currentPassword: "Current password",
      newPassword: "New password",
      passwordMin: "At least 8 characters.",
      passwordSave: "Update password",
      passwordSaving: "Updating…",
      passwordSaved: "Password updated. Other sessions were signed out.",
      passwordError: "Couldn't update the password — check your current password.",
    },
  },
  teamErrors: {
    already_member: "That address already belongs to a team member.",
    last_admin: "An organization needs at least one admin — promote someone else first.",
    cannot_remove_self: "You can't remove your own membership.",
    rate_limited: "Too many attempts — try again in a bit.",
    forbidden: "Only admins can manage the team.",
    generic: "That didn't work — try again.",
  },
  dashboard: {
    title: "Overview",
    subtitlePipeline: "{{name}} — pipeline at a glance",
    yourWorkspace: "Your workspace",
    activeDeals: "Active deals",
    won: "Won",
    needAction: "Need action",
    pipelineValue: "Pipeline value",
    ownFundsGap: "{{amount}} own-funds gap",
    fullyFinanced: "fully financed",
    needsAttention: "Needs attention",
    recentActivity: "Recent activity",
    nothingBlocked: "Nothing blocked. Smooth sailing.",
    noDeals: "No deals yet.",
    blocked: "{{count}} blocked",
  },
  clients: {
    title: "Clients",
    subtitle_one: "{{count}} deal across your pipeline",
    subtitle_other: "{{count}} deals across your pipeline",
    filterAll: "All",
    filterActive: "Active",
    filterWon: "Won",
    filterOnHold: "On hold",
    searchPlaceholder: "Search…",
    noMatch: "No deals match these filters.",
    filterFinancing: "Financing",
    finAll: "All financing",
    finGap: "Has funding gap",
    finFinanced: "Fully financed",
    sort: "Sort",
    sortRecent: "Recent activity",
    sortName: "Client name",
    sortValue: "Pipeline value",
    sortGap: "Funding gap",
    colDeal: "Deal",
    colStatus: "Status",
    colProgress: "Progress",
    colFinancing: "Financing",
    colNextAction: "Next action",
    colActivity: "Activity",
    blocked: "{{count}} blocked",
    gap: "{{amount}} gap",
    funded: "Funded",
    rowMenu: "Deal actions",
    openDeal: "Open deal",
    setStatus: "Set status",
    deleteDeal: "Delete deal",
    deleteConfirm: "Delete {{name}}'s deal? This removes the canvas, documents and comments.",
    emptyTitle: "No deals here yet",
    emptyBody:
      "Create your first deal and the standard buyer journey lands on the canvas, ready to drive.",
  },
  login: {
    createTitle: "Create your account",
    signInTitle: "Sign in",
    createSubtitle: "Set up your advisor account to get started.",
    signInSubtitle: "Welcome back. Pick up where you left off.",
    yourName: "Your name",
    workEmail: "Work email",
    emailPlaceholder: "you@agency.nl",
    password: "Password",
    createAccount: "Create account",
    continue: "Continue",
    alreadyHaveAccount: "Already have an account?",
    noAccountYet: "No account yet?",
    createOne: "Create one",
    errCreate: "Could not create your account. Please try again.",
    errSignin: "Could not sign in. Check your email and password.",
    errExists: "An account with this email already exists. Sign in instead.",
    errPasswordLength: "Password must be at least 8 characters.",
    forgotPassword: "Forgot password?",
    forgotTitle: "Reset your password",
    forgotSubtitle: "Enter your email and we'll send you a reset link.",
    forgotSubmit: "Send reset link",
    forgotSent: "If an account exists for that address, a reset link is on its way. Check your inbox.",
    backToSignIn: "Back to sign in",
    resetTitle: "Choose a new password",
    resetSubtitle: "Almost there — set a new password for your account.",
    resetNewPassword: "New password",
    resetSubmit: "Set new password",
    resetSuccess: "Password updated. Redirecting you to sign in…",
    resetInvalidToken: "This reset link is invalid or has expired. Request a new one.",
  },
  verifyEmail: {
    title: "Verify your email",
    body: "We sent a verification link to",
    checkInbox:
      "Open the email and click the link to activate your account. Check your spam folder if it hasn't arrived.",
    resend: "Resend email",
    changeEmail: "Use a different email",
    resendCooldown: "Sent — check your inbox.",
    resendError: "Could not resend the email. Try again in a moment.",
  },
  verified: {
    successTitle: "Email verified",
    successBody: "Your email is confirmed. You're all set.",
    failedTitle: "Verification failed",
    failedBody: "This link is invalid or has expired. Request a new one.",
    ctaWorkspace: "Open the workspace",
    ctaSignin: "Sign in",
    ctaResend: "Resend verification email",
  },
  onboarding: {
    title: "Create your workspace",
    subtitle: "Name your team workspace. You can change it later in Settings.",
    nameLabel: "Workspace name",
    nameHint: "Leave blank to use your name.",
    error: "Could not create the workspace. Please try again.",
    creating: "Creating…",
    create: "Create workspace",
  },
  start: {
    // chrome
    stepOf: "Step {{current}} of {{total}}",
    back: "Back",
    continue: "Continue",
    skip: "Skip for now",
    finishing: "Setting up…",
    haveAccount: "Already have an account?",
    noAccount: "No account yet?",
    signInLink: "Sign in",
    createLink: "Create one",
    // plan
    planTitle: "Choose your plan",
    planSubtitle: "Start with a 7-day free trial. No credit card required — cancel anytime.",
    planPopular: "Most popular",
    planMonth: "/mo",
    planPerSeat: "per seat · billed monthly",
    planContinue: "Continue with {{plan}}",
    planTrialNote: "No credit card required — you won't be charged during your 7-day trial.",
    // account
    accountTitle: "Create your account",
    accountSubtitle: "This is your personal login for Biedradar Pro.",
    accountSigninTitle: "Sign in",
    accountSigninSubtitle: "Welcome back — let's finish setting things up.",
    name: "Your name",
    email: "Work email",
    emailPlaceholder: "you@agency.nl",
    password: "Password",
    creatingAccount: "Creating your account…",
    errAccount: "Could not create your account. Please try again.",
    errExists: "That email already has an account — sign in to continue.",
    errSignin: "Could not sign in. Check your email and password.",
    // workspace
    workspaceTitle: "Set up your workspace",
    workspaceSubtitle: "A little context so we can tailor Biedradar to your team.",
    workspaceName: "Workspace name",
    workspaceNameHint: "Usually your agency or company name.",
    workspaceNamePlaceholder: "e.g. Hendriks Hypotheken",
    teamSize: "How big is your team?",
    teamSize_solo: "Just me",
    teamSize_small: "2–5",
    teamSize_medium: "6–20",
    teamSize_large: "20+",
    role: "What's your role?",
    role_advisor: "Mortgage advisor",
    role_agent: "Estate agent",
    role_manager: "Team lead / owner",
    role_other: "Something else",
    errWorkspace: "Could not create the workspace. Please try again.",
    // invite
    inviteTitle: "Invite your team",
    inviteSubtitle: "Add colleagues now, or skip and invite them later from Settings.",
    inviteEmail: "colleague@agency.nl",
    inviteRoleLabel: "Role",
    inviteRole_admin: "Admin",
    inviteRole_advisor: "Advisor",
    inviteRole_assistant: "Assistant",
    inviteAdd: "Add another",
    inviteSend: "Send invites",
    inviteSending: "Sending invites…",
    errInvite: "Some invites couldn't be sent. Check the addresses and try again.",
    // done
    doneTitle: "You're all set",
    doneSubtitle: "Your workspace is ready — your free trial has already started.",
    doneVerifyNote:
      "One more thing: we've emailed a confirmation link to {{email}}. Verify your email to open your workspace — check your spam folder too.",
    doneTrialNote: "Your 7-day free trial of the {{plan}} plan has started — no credit card required. Upgrade anytime via Settings → Billing.",
    doneTrialCta: "Start {{plan}} trial",
    redirecting: "Redirecting to checkout…",
    errTrial: "Couldn't start the trial just now — your workspace still works; try again from Settings → Billing.",
    doneCta: "Open your workspace",
  },
  billing: {
    title: "Billing",
    loading: "Loading…",
    trialLeft_one: "Free trial — {{count}} day left · cancel before then and you won't be charged",
    trialLeft_other: "Free trial — {{count}} days left · cancel before then and you won't be charged",
    startTrial: "Start 7-day free trial",
    starting: "Starting…",
    seatsUsed: "Seats used",
    seatsOf: "{{used}} of {{included}} included",
    extraSeats_one: "{{count}} extra seat · +{{amount}}/mo",
    extraSeats_other: "{{count}} extra seats · +{{amount}}/mo",
    usageTitle: "Usage this month",
    usageActiveClients: "Active clients",
    usageNewClients: "New clients",
    usageUploads: "Document uploads",
    usageSigning: "Signing requests",
    planLine: "Plan ({{seats}} seats incl.)",
    seatOverage: "Seat overage",
    total: "Total",
    plansTitle: "Plans",
    noOnlinePayment: "We couldn't start online checkout just now — please try again in a moment.",
    billedNote: "7-day free trial, no credit card required — billed monthly via Mollie only after you upgrade. 5 seats included per plan, then €5/seat. Cancel anytime.",
    perMonth: "/mo",
    noPlan: "No plan",
    planSuffix: "{{name}} plan",
    proPlan: "Pro plan",
    current: "Current",
    currentPlan: "Current plan",
    redirecting: "Redirecting…",
    subscribe: "Subscribe",
    unlimited: "Unlimited",
    status: { none: "No plan", trialing: "Trial", active: "Active", past_due: "Past due", canceled: "Canceled" },
    rowActiveClients: "Active clients",
    rowNewClients: "New clients / mo",
    rowUploads: "Uploads / mo",
    rowSigning: "Signing / mo",
  },
  status: {
    deal: { active: "Active", won: "Won", lost: "Lost", on_hold: "On hold" },
    step: {
      not_started: "Not started",
      in_progress: "In progress",
      blocked: "Blocked",
      done: "Done",
    },
  },
  roles: {
    admin: "Admin",
    advisor: "Advisor",
    assistant: "Assistant",
    client: "Client",
  },
  team: {
    title: "Team",
    seatsUsed: "{{used}} of {{total}} included seats used",
    invite: "Invite member",
    inviteCancel: "Cancel",
    role: "Role",
    you: "you",
    remove: "Remove member",
    removeConfirm: "Remove {{name}} from this workspace?",
    pendingTitle: "Pending invites",
    revoke: "Revoke",
    seatsFootnote: "5 seats included in every plan · each extra seat is billed monthly.",
    emailPlaceholder: "teammate@agency.nl",
    send: "Send invite",
    sending: "Sending…",
    inviteLinkLabel: "Invite link (also emailed) — share it to let them join:",
    copied: "Copied!",
  },
  // Canonical stage names, keyed by StepTypeKey. Reused by the portfolio
  // "next action" column and the canvas (add-step menu / nodes).
  stepType: {
    property: "Property & viewing",
    report: "Biedradar report",
    offer: "Offer / bid",
    offer_accepted: "Offer accepted",
    appraisal: "Appraisal (taxatie)",
    mortgage: "Mortgage / bank",
    notary: "Notary / passing",
    sign: "Sign document",
    document_request: "Document request",
    financials: "Financials",
    custom: "Custom step",
  },
  nextAction: {
    won: "Passed at the notary",
    ready: "Ready to complete",
    blocked: "Blocked · {{step}}",
  },
  time: { justNow: "just now" },
  portal: {
    loading: "Loading…",
    openingSpace: "Opening your space…",
    inviteInvalid: "This invite link is invalid or has expired.",
    loadingSpace: "Loading your space…",
    spaceUnavailable: "This space is unavailable, or you don't have access to it.",
    loadingSpaces: "Loading your spaces…",
    readOnlyBadge: "Read-only client space",
    journey: "Your purchase journey",
    stepsProgress_one: "{{done}} of {{total}} step",
    stepsProgress_other: "{{done}} of {{total}} steps",
    documents: "Documents",
    documentsEmpty: "No documents yet.",
    uploadDocument: "Upload a document",
    uploading: "Uploading…",
    requestedByAdvisor: "Requested by your advisor",
    received: "Received",
    upload: "Upload",
    messages: "Messages",
    messagesEmpty: "No messages yet.",
    messagePlaceholder: "Write a message…",
    send: "Send",
    sending: "Sending…",
    toSign: "To sign",
    signed: "Signed",
    awaitingSignature: "Awaiting signature — check your email",
    home: {
      title: "Your spaces",
      signedInAs: "Signed in as {{name}}. ",
      pickPurchase: "Pick a purchase to view.",
      emptyBody: "You don't have any spaces yet. When your advisor shares one with you, it will show up here.",
      emptyTip: "Tip: open the invite link your advisor emailed you to get started.",
    },
    auth: {
      heading: "Your home-purchase space",
      inviteSubheading: "Sign in or create an account to open the space your advisor shared with you.",
      subheadingRegister: "Create an account to follow your purchase, share documents and message your advisor.",
      subheadingSignIn: "Sign in to follow your purchase, share documents and message your advisor.",
      nameLabel: "Your name",
      namePlaceholder: "Anna Bakker",
      emailLabel: "Email",
      emailPlaceholder: "you@email.nl",
      passwordLabel: "Password",
      createAccount: "Create account",
      continue: "Continue",
      alreadyHaveAccount: "Already have an account?",
      firstTimeHere: "First time here?",
      signIn: "Sign in",
      createOne: "Create one",
      errCreate: "Could not create your account. Please try again.",
      errSignIn: "Could not sign in. Check your email and password.",
    },
  },
  lang: { label: "Language", nl: "Nederlands", en: "English" },
  stepTypeDesc: {
    property: "The target home and viewings",
    report: "Pull the data report into the deal",
    offer: "Track the offer and its conditions",
    offer_accepted: "Acceptance milestone",
    appraisal: "NWWI appraisal and value",
    mortgage: "Mortgage application milestones",
    notary: "Closing at the notary",
    sign: "Send a document out to be signed",
    document_request: "Ask the client for files",
    financials: "Affordability snapshot and the gap",
    custom: "Anything you define",
  },
  canvas: {
    addStep: "Add step",
    internal: "internal",
    internalHint: "Internal — hidden from the client portal",
    sellingAgent: "Selling agent",
    deleteConnection: "Delete connection",
    stepPanel: {
      close: "Close",
      tabDetails: "Details",
      tabNotes: "Notes",
      tabFiles: "Documents",
      tabComments: "Comments",
      notesHint: "Rich notes — visible to your team (not the client portal).",
      locationHeader: "Location",
      locationNoCoords: "No coordinates yet — add the address via autocomplete to pin it on the map.",
      openInMaps: "Open in maps",
      bidConditions: "Conditions",
      bidConditionsPlaceholder: "Financing, inspection…",
      labelTitle: "Title",
      labelStatus: "Status",
      labelNotes: "Notes",
      labelVisibility: "Client portal visibility",
      notesPlaceholder: "Add an internal note…",
      visibilityShared: "shared",
      visibilityInternal: "internal",
      visibilitySharedHint: "Visible to the client in the read-only portal.",
      visibilityInternalHint: "Hidden from the client portal.",
      markComplete: "Mark complete",
      deleteStep: "Delete step",
      noFiles: "No documents on this step yet.",
      uploadFile: "Upload a document",
      uploading: "Uploading…",
      requestFilePlaceholder: "Request a document from the client…",
      requestFile: "Request",
      noComments: "No comments yet. Start the thread.",
      commentPlaceholder: "Write a comment…",
      comment: "Comment",
      posting: "Posting…",
      clientBadge: "client",
      requestedFrom: "Requested from client",
      download: "Download",
      upload: "Upload",
      markInReview: "Mark in review",
      approve: "Approve",
      docRequested: "Requested",
      docReceived: "Received",
      docInReview: "In review",
      docApproved: "Approved",
      signatureHeader: "Signature",
      signDocTitlePlaceholder: "Document title",
      signSignerPlaceholder: "Signer name",
      signNoDoc: "No document — acknowledgement only",
      createSigningLink: "Create signing link",
      creatingSigningLink: "Creating…",
      copied: "Copied!",
      financingHeader: "Financing",
      financingAskingPrice: "Asking price",
      financingLatestBid: "Latest bid",
      financingEstimatedValue: "Estimated value",
      financingMaxMortgage: "Max mortgage",
      financingOwnFundsGap: "Own-funds gap",
      financingFullyFinanced: "Fully financed",
      reportHeader: "Biedradar report",
      reportBody:
        "Pull the Kadaster-backed valuation, comparables and appraisal-risk for {{address}} into the deal.",
      pullReport: "Pull report",
      agentLink: "Selling agent",
      agentLinkNone: "No agent linked",
      agentAddNew: "New selling agent",
      agentAddConfirm: "Add agent",
      agentLinkAddFirst: "Add a selling agent in the panel on the canvas first, then link it here.",
      stepAddressPlaceholder: "Set this property's address…",
      stepAddressHint: "Each property step can have its own address — separate from the deal's main target.",
    },
  },
};

const nl: typeof en = {
  common: {
    signIn: "Inloggen",
    openWorkspace: "Open werkruimte",
    openTheWorkspace: "Open de werkruimte",
    signOut: "Uitloggen",
    signingOut: "Uitloggen…",
    newDeal: "Nieuwe deal",
    cancel: "Annuleren",
    close: "Sluiten",
    loading: "Laden…",
  },
  blog: {
    backToApp: "Naar Biedradar Pro →",
    indexTitle: "Blog",
    indexSubtitle:
      "Analyses, cijfers en praktische gidsen over bieden en de Nederlandse woningmarkt.",
    readMore: "Lees meer",
    minRead: "min",
    readingTime: "minuten leestijd",
    breadcrumb: "Kruimelpad",
    related: "Gerelateerde gidsen",
    readArticle: "Lees artikel",
  },
  nav: {
    overview: "Overzicht",
    clients: "Klanten",
    properties: "Woningen",
    sellers: "Makelaars",
    agenda: "Agenda",
    documents: "Documenten",
    team: "Team",
    settings: "Instellingen",
    workspace: "Werkruimte",
    planWorkspace: "{{plan}} werkruimte",
    plan_free: "Gratis",
    plan_starter: "Starter",
    plan_pro: "Pro",
    plan_scale: "Scale",
    trialTitle: "Gratis proefperiode",
    trialDaysLeft_one: "Nog {{count}} dag",
    trialDaysLeft_other: "Nog {{count}} dagen",
    trialExpiredTitle: "Proefperiode afgelopen",
    trialExpiredBody: "Upgrade om alles te blijven gebruiken.",
    trialUpgrade: "Upgraden",
  },
  properties: {
    title: "Woningen",
    subtitle: "{{count}} woningen over je deals",
    search: "Zoek adres, klant, makelaar…",
    filterStatus: "Dealstatus",
    statusAll: "Alle statussen",
    filterViewed: "Bezichtiging",
    viewedAll: "Alle",
    colProperty: "Woning",
    colClient: "Klant",
    colStatus: "Deal",
    colViewing: "Bezichtiging",
    colAgent: "Verkopend makelaar",
    loading: "Laden…",
    empty: "Nog geen woningen. Voeg een woning & bezichtiging-stap toe aan een deal.",
    notFound: "Deze woning kon niet worden gevonden.",
    back: "Terug naar woningen",
    badgeViewed: "Bezichtigd",
    badgePlanned: "Gepland",
    notViewed: "Nog niet bezichtigd",
    noAgent: "Geen makelaar gekoppeld",
    cardViewing: "Bezichtiging",
    cardAgent: "Verkopend makelaar",
    cardClient: "Klant",
    cardDeal: "Deal",
    openDeal: "Deal openen",
    seeClient: "Klant-space bekijken",
    remove: "Woning verwijderen",
    removeConfirm: "De woningstap voor {{address}} verwijderen?",
  },
  sellers: {
    title: "Verkopend makelaars",
    subtitle: "{{count}} makelaars over je deals",
    search: "Zoek makelaar, kantoor, klant…",
    filterAgency: "Kantoor",
    allAgencies: "Alle kantoren",
    filterLead: "Hoofd",
    leadAll: "Alle makelaars",
    leadOnly: "Alleen hoofd",
    colAgent: "Makelaar",
    colClient: "Klant",
    colProperty: "Woning",
    colContact: "Contact",
    loading: "Laden…",
    empty: "Nog geen makelaars. Voeg er een toe op de woningstap van een deal.",
    notFound: "Deze makelaar kon niet worden gevonden.",
    back: "Terug naar makelaars",
    lead: "Hoofd",
    cardClient: "Klant",
    cardProperty: "Woning",
    cardDeal: "Deal",
    openDeal: "Deal openen",
    remove: "Makelaar verwijderen",
    removeConfirm: "{{name}} van deze deal verwijderen?",
  },
  agenda: {
    title: "Agenda",
    subtitle: "Alle bezichtigingen over je deals, op datum.",
    empty: "Nog geen bezichtigingen gepland. Voeg ze toe op de woningstap van een deal.",
    noDate: "Geen datum",
    bucket: { today: "Vandaag", week: "Deze week", later: "Later", none: "Geen datum", past: "Geweest" },
    search: "Zoek klant, adres…",
    filterStatus: "Status",
    statusAll: "Alle statussen",
  },
  cmdk: {
    placeholder: "Zoek deals, klanten, pagina's…",
    empty: "Geen resultaten.",
    pages: "Pagina's",
    deals: "Deals & klanten",
    hintNavigate: "navigeren",
    hintOpen: "openen",
    hintClose: "sluiten",
  },
  view: {
    flow: "Stroomschema",
    board: "Bord",
    hintTitle: "Twee weergaven",
    hintBody: "Bekijk deze space als stroomschema of als Trello-bord. Wissel hier wanneer je wilt.",
    hintGotIt: "Begrepen",
  },
  sign: {
    loading: "Laden…",
    invalid: "Deze ondertekenlink is ongeldig of verlopen.",
    signedTitle: "Ondertekend",
    signedBody: "Bedankt, {{name}}. Je handtekening voor “{{title}}” is vastgelegd.",
    asksYouToSign: "{{org}} vraagt je om te ondertekenen",
    documentLabel: "Document",
    openDocument: "Document openen in nieuw tabblad",
    yourSignature: "Je handtekening",
    drawHere: "Teken hier je handtekening",
    clear: "Wissen",
    consent: "Door te ondertekenen gaat {{name}} ermee akkoord dat deze elektronische handtekening rechtsgeldig is.",
    signButton: "Document ondertekenen",
    signing: "Ondertekenen…",
    failed: "Ondertekenen mislukt. Controleer je verbinding en probeer het opnieuw.",
    auditNote: "Een tijdstempel-audit (tijd, IP) wordt bij je handtekening bewaard.",
    poweredBy: "Mogelijk gemaakt door Biedradar Pro · interne ondertekening",
    signedOn: "Ondertekend op {{date}}",
    typeTab: "Typen",
    drawTab: "Tekenen",
    typedPlaceholder: "Typ je volledige naam",
    typedPreviewHint: "Je handtekening verschijnt hier",
    download: "Downloaden",
    loadingDocument: "Document laden…",
    previewUnavailable: "Dit document kan hier niet worden weergegeven.",
    signHere: "Teken hier",
    placedHint: "Je adviseur heeft aangegeven waar je moet tekenen — zie het gemarkeerde vak hierboven.",
  },
  documents: {
    title: "Documenten",
    subtitle: "{{count}} bestanden over je deals",
    request: "Aanvragen",
    requestDoc: "Document opvragen",
    requestSignature: "Handtekening vragen",
    search: "Zoek bestanden, klanten…",
    filterStatus: "Status",
    filterDeal: "Deal",
    filterKind: "Soort",
    filterSignature: "Handtekening",
    sort: "Sorteer",
    statusAll: "Alle statussen",
    statusRequested: "Opgevraagd",
    statusReceived: "Ontvangen",
    statusInReview: "In review",
    statusApproved: "Goedgekeurd",
    dealAll: "Alle deals",
    kindAll: "Alle soorten",
    kindFile: "Bestanden",
    kindAwaiting: "Wacht op upload",
    sigAll: "Alle handtekeningen",
    sigPending: "Te tekenen",
    sigSigned: "Getekend",
    sigBadgePending: "Te tekenen",
    sigBadgeSigned: "Getekend",
    sigBadgeExpired: "Verlopen",
    sortRecent: "Meest recent",
    sortName: "Naam (A–Z)",
    sortStatus: "Status",
    colFile: "Bestand",
    colDeal: "Deal",
    colStatus: "Status",
    colSignature: "Handtekening",
    colUploaded: "Geüpload",
    awaitingUpload: "wacht op upload",
    download: "Downloaden",
    loading: "Laden…",
    empty: "Geen documenten met deze filters.",
    requestDocTitle: "Document opvragen",
    requestSignatureTitle: "Handtekening vragen",
    requestDocConfirm: "Verzoek versturen",
    requestSignatureConfirm: "Ondertekenlink maken",
    done: "Klaar",
    fieldDeal: "Deal",
    fieldDealPlaceholder: "Kies een deal…",
    fieldDocument: "Te ondertekenen document",
    fieldDocumentNone: "Geen document — alleen akkoord",
    fieldName: "Wat opvragen",
    fieldNamePlaceholder: "bijv. Inkomensverklaring",
    fieldTitle: "Titel",
    fieldTitlePlaceholder: "bijv. Opdracht tot dienstverlening",
    fieldSigner: "Ondertekenaar",
    fieldSignerPlaceholder: "Volledige naam",
    signatureLinkReady: "Ondertekenlink voor {{name}} staat klaar — deel hem:",
    copied: "Gekopieerd!",
    placement: "Plek handtekening",
    placeHint: "Klik of sleep op het document om aan te geven waar de handtekening komt.",
    placeTitle: "Plaats de handtekening",
    placeNoDoc: "Kies eerst een document om de handtekening te plaatsen.",
    placementAdd: "Op document plaatsen",
    placementEdit: "Handtekeningvak verplaatsen",
    placementClear: "Verwijderen",
    placementSet: "Geplaatst op pagina {{page}}",
    placementDone: "Deze plek gebruiken",
    placementSkip: "Plaatsen overslaan",
  },
  landing: {
    seoLang: "nl",
    seoTitle: "Biedradar — slimmer bieden op een Nederlands huis",
    seoDescription:
      "Biedradar maakt van Kadaster-, CBS- en PDOK-data een helder biedadvies — vergelijkbare verkopen, overbiedstatistieken, taxatierisico en een hypotheekschatting. Pro-werkplek voor adviseurs en kantoren.",
    heroShotZoom: "klik om te vergroten",
    heroPrev: "Vorige schermafbeelding",
    heroNext: "Volgende schermafbeelding",
    heroShots: {
      dashboard: "Pijplijn-overzicht",
      canvas: "Deal-canvas",
      dealDetail: "Stapdetails & financiering",
      clients: "Klanten",
      properties: "Woningen",
      sellers: "Verkopende makelaars",
      team: "Team & seats",
      command: "Commandopalet",
      settings: "Werkruimte-instellingen",
    },
    navFeatures: "Functies",
    navBlog: "Blog",
    signIn: "Inloggen",
    badge: "Voor hypotheekadviseurs & makelaars",
    titleLine1: "Begeleid elke deal van",
    titleLine2: "bod tot sleutel.",
    subtitle:
      "Biedradar Pro is de deal-werkruimte voor Nederlands vastgoed — begeleid elke woningaankoop van het eerste bod tot de notaris, met het financieringsgat, documenten, ondertekenen en een klantportaal op één plek.",
    ctaPrimary: "Nu gratis starten",
    ctaSecondary: "Inloggen",
    ctaNote: "7 dagen gratis proberen · Maandelijks opzegbaar",
    heroShotAlt: "Het Biedradar Pro-dashboard",
    trustLabel: "Gebouwd op officiële Nederlandse bronnen",
    canvasEyebrow: "Het deal-canvas",
    canvasTitle: "Elke deal als een levende flow",
    canvasBody:
      "Sleep elke stap van bod tot sleutel op één canvas. Zie wat klaar is, wat vastloopt en wat er nu moet gebeuren — voor elke klant, in één oogopslag.",
    canvasB1: "Kooptraject, taxatie, hypotheek, notaris — kant-en-klaar",
    canvasB2: "Realtime, zodat je hele team hetzelfde bord ziet",
    canvasB3: "Flow- of Trello-bord — wissel wanneer je wilt",
    canvasShotAlt: "Een deal op het Biedradar Pro-canvas",
    pipelineEyebrow: "Overzicht",
    pipelineTitle: "Je hele pijplijn in één oogopslag",
    pipelineBody:
      "Pijplijnwaarde, de deals die actie nodig hebben en wat er gewonnen is — zonder spreadsheet. Het dashboard laat zien welke deals vandaag jouw aandacht vragen.",
    pipelineB1: "Live pijplijnwaarde en eigen-geld-gat",
    pipelineB2: "Blokkades en recente activiteit, vooraan",
    pipelineB3: "Elke klant en woning, doorzoekbaar",
    pipelineShotAlt: "De Biedradar Pro-pijplijn",
    portalEyebrow: "Klantportaal",
    portalTitle: "Neem je klant mee in de deal",
    portalBody:
      "Deel een overzichtelijke, alleen-lezen ruimte zodat kopers de voortgang volgen, documenten uploaden en ondertekenen — geen mailwisselingen, geen achternazitten.",
    portalB1: "Alleen-lezen ruimte, in jouw huisstijl",
    portalB2: "Documentverzoeken en uploads",
    portalB3: "Ondertekenen ingebouwd",
    featuresTitle: "Alles wat een deal nodig heeft",
    featuresSubtitle:
      "Van het eerste bod tot de sleutel — zonder de werkruimte te verlaten.",
    f1Title: "Financieringsgat",
    f1Body:
      "Zie het eigen-geld-tekort op elke deal, zodra de cijfers veranderen.",
    f2Title: "Ondertekenen",
    f2Body:
      "Stuur en onderteken documenten in de browser — precies op de juiste plek.",
    f3Title: "Documenten",
    f3Body:
      "Vraag op, verzamel en volg elk bestand per stap, met status in één oogopslag.",
    f4Title: "Bezichtigingsagenda",
    f4Body: "Elke bezichtiging over al je deals in één agenda.",
    f5Title: "Realtime team",
    f5Body: "Iedereen ziet hetzelfde bord, dat live meebeweegt met de deal.",
    f6Title: "Deal-sjablonen",
    f6Body: "Start een nieuwe deal vanuit je eigen traject in één klik.",
    ctaTitle: "Run elke deal als een pro.",
    ctaBody:
      "Open de werkruimte en begeleid je volgende klant van bod tot sleutel.",
    ctaButton: "Nu gratis starten",
    ctaFootnote: "7 dagen gratis proberen · Maandelijks opzegbaar · EU-gehost",
    socialProof: "Vertrouwd door 130+ adviseurs & makelaars",
    marqueeLabel: "Gebruikt door makelaars en adviseurs door heel Nederland",
    stat1Value: "€25M+",
    stat1Label: "aan vastgoed per jaar begeleid",
    stat2Value: "130+",
    stat2Label: "makelaars & adviseurs aan boord",
    stat3Value: "2.000+",
    stat3Label: "kopers naar de sleutel begeleid",
    stat4Value: "4,9/5",
    stat4Label: "gemiddelde beoordeling",
    testimonialsEyebrow: "Geliefd bij makelaars",
    testimonialsTitle: "Gebouwd voor hoe je écht werkt",
    testimonialsSubtitle:
      "Hypotheekadviseurs en aankoopmakelaars door heel Nederland runnen hun deals op Biedradar Pro.",
    t1Quote:
      "Vroeger liep een deal via mail, WhatsApp en drie spreadsheets. Nu ziet mijn hele team één bord — en weten mijn klanten eindelijk waar ze aan toe zijn.",
    t1Name: "Sanne de Vries",
    t1Role: "Aankoopmakelaar · De Sleutel Aankoop",
    t2Quote:
      "Het financieringsgat op elke deal, zodra de cijfers veranderen. Het eerste wat ik 's ochtends bekijk.",
    t2Name: "Thomas Bakker",
    t2Role: "Hypotheekadviseur · Amstelhaven Hypotheken",
    t3Quote:
      "Een overzichtelijk klantportaal delen in plaats van eindeloze mailwisselingen scheelt me uren per week. Kopers zijn er dol op.",
    t3Name: "Yasmin El Amrani",
    t3Role: "Aankoopmakelaar · Polderhuis Makelaardij",
    t4Quote:
      "Het financieringsgat en elk wat-als-scenario op één plek. Ik stap elke biedronde in en weet precies wat mijn klant kan dragen.",
    t4Name: "Daan Koster",
    t4Role: "Hypotheekadviseur · Noorderlicht Hypotheken",
    t5Quote:
      "Klanten tekenen de koopovereenkomst binnen enkele minuten vanaf hun telefoon. Geen printen, geen scannen, geen achtervolgen — het is gewoon klaar.",
    t5Name: "Lotte Visser",
    t5Role: "Aankoopmakelaar · Grachtwerk Advies",
    t6Quote:
      "We hebben acht adviseurs in een week ingewerkt. Iedereen werkt nu hetzelfde, en ik zie eindelijk de pijplijn van het hele kantoor in één oogopslag.",
    t6Name: "Mehmet Yıldız",
    t6Role: "Kantooreigenaar · Stadshuis Makelaars",
    t7Quote:
      "Vroeger betekende een deal die tussen wal en schip viel een gemiste courtage. Nu beweegt er niets zonder dat ik het zie. Mijn opvolging is enorm verbeterd.",
    t7Name: "Iris Hendriks",
    t7Role: "Aankoopmakelaar · Veldhuis & Co",
    t8Quote:
      "De WOZ- en overbiedcontext komt automatisch in het rapport. Ik haal geen cijfers meer met de hand op en heb twee avonden per week teruggekregen.",
    t8Name: "Bram Mulder",
    t8Role: "Hypotheekadviseur · Rivierhof Hypotheken",
    t9Quote:
      "Mijn klanten belden me vroeger gespannen voor elke biedronde. Nu openen ze het portaal en zien precies hoe het ervoor staat. De telefoontjes zijn vrijwel gestopt.",
    t9Name: "Fatima Ouali",
    t9Role: "Aankoopmakelaar · Binnenstad Aankoop",
    t10Quote:
      "Ik heb in een maand drie deals gesloten die ik normaal had laten vallen. Het pijplijnoverzicht laat simpelweg niets koud worden.",
    t10Name: "Sven Jansen",
    t10Role: "Aankoopmakelaar · Duinrand Makelaardij",
    t11Quote:
      "We runnen twee kantoren vanaf één bord. Ik zie wie overbelast is en kan herverdelen voordat een klant ooit vertraging merkt.",
    t11Name: "Anouk Smit",
    t11Role: "Kantooreigenaar · Westerpoort Groep",
    t12Quote:
      "De financieringsscenario's zijn dé killer-feature. Ik reken drie biedingen live door met een klant en ze beslissen in minuten, niet in dagen.",
    t12Name: "Ravi Pratap",
    t12Role: "Hypotheekadviseur · Zonnehuis Financieel",
    t13Quote:
      "Tekenen ging van een week papierwerk achterna zitten naar dezelfde middag. Kopers zijn oprecht verrast hoe snel het gaat.",
    t13Name: "Marit Visscher",
    t13Role: "Aankoopmakelaar · Hanze Aankoop",
    t14Quote:
      "Ik ben overgestapt van een generiek CRM dat Nederlandse deals nooit begreep. Biedradar spreekt gewoon de taal — WOZ, NHG, overbieden, alles.",
    t14Name: "Joost van Dijk",
    t14Role: "Hypotheekadviseur · Maasstad Hypotheken",
    t15Quote:
      "Een nieuwe adviseur inwerken kostte vroeger een maand begeleiding. Nu zijn ze op dag twee productief omdat het bord zichzelf uitlegt.",
    t15Name: "Selin Demir",
    t15Role: "Kantooreigenaar · Parkzicht Makelaars",
    t16Quote:
      "Ik doe twee keer zoveel deals als vorig jaar in dezelfde uren. Het is de eerste tool die echt met me meegroeit in plaats van me af te remmen.",
    t16Name: "Pieter de Groot",
    t16Role: "Aankoopmakelaar · Lindelaan Advies",
    t17Quote:
      "Het mobiele portaal heeft mijn klanten over de streep getrokken. Ze tekenen in de trein, uploaden documenten vanaf de keukentafel. Nul wrijving.",
    t17Name: "Nadia Bakir",
    t17Role: "Hypotheekadviseur · Havenkwartier Hypotheken",
    t18Quote:
      "Ik vertrouw de taxatierisico-melding inmiddels meer dan mijn onderbuikgevoel. Hij ving twee deals op die richting een financieringsprobleem gingen voordat het misging.",
    t18Name: "Erik Schouten",
    t18Role: "Aankoopmakelaar · Vesting Makelaardij",
    faqEyebrow: "Veelgestelde vragen",
    faqTitle: "Vragen, beantwoord",
    q1: "Voor wie is Biedradar Pro?",
    a1: "Voor Nederlandse hypotheekadviseurs en aankoopmakelaars die kopers begeleiden van het eerste bod tot de notaris.",
    q2: "Werkt het met mijn hele team?",
    a2: "Ja — elke deal is een gedeeld, realtime bord, zodat je team altijd dezelfde status live ziet.",
    q3: "Kan mijn klant meekijken?",
    a3: "Deel een overzichtelijk, alleen-lezen klantportaal waar kopers de voortgang volgen, documenten uploaden en ondertekenen — in jouw huisstijl.",
    q4: "Waar staat mijn data?",
    a4: "Op EU-gehoste infrastructuur in Frankfurt, AVG-proof. De gegevens van je klanten blijven in de EER.",
    q5: "Wat kost het?",
    a5: "Pakketten beginnen bij €49/maand inclusief 5 seats, met 7 dagen gratis proberen en geen lock-in. Bekijk de prijzenpagina voor het volledige overzicht.",
    q6: "Hoe werkt de prijsstelling?",
    a6: "Pakketten beginnen bij €49/maand inclusief 5 seats. Meer mensen nodig? Extra seats kosten €5/seat/maand. Kies de tier die past bij je klantvolume.",
    q7: "Is er een gratis proefperiode?",
    a7: "Ja — elk pakket start met 7 dagen gratis proberen, zonder creditcard. Voeg een betaalmethode toe wanneer jij er klaar voor bent via Instellingen → Facturering; tot die tijd wordt er simpelweg niets in rekening gebracht.",
    q8: "Kan ik maandelijks opzeggen?",
    a8: "Ja. Er is geen lock-in — zeg op via je facturatie-instellingen wanneer je wilt en je wordt niet opnieuw gefactureerd.",
    q9: "Is het ondertekenen rechtsgeldig?",
    a9: "Ja. Elke handtekening wordt vastgelegd met een volledige audit trail (tijdstip, IP, document-hash) — een geldige elektronische handtekening onder eIDAS voor koopovereenkomsten.",
    q10: "Kunnen klanten veilig documenten uploaden?",
    a10: "Kopers uploaden loonstroken, ID en bankafschriften rechtstreeks in hun eigen portaal — versleuteld onderweg en in opslag, nooit via e-mail.",
    q11: "Waar komt de woningdata vandaan?",
    a11: "Dezelfde officiële Nederlandse bronnen als Biedradar: Kadaster, CBS, PDOK, EP-Online en Politie. Vergelijkbare verkopen, WOZ en buurtdata staan in elke deal.",
    q12: "Hoe ga ik van start?",
    a12: "Kies een pakket om je proefperiode van 7 dagen te starten (geen creditcard nodig), nodig je team uit en maak je eerste deal — in enkele minuten. Importeer bestaande klanten onderweg; er is niets te installeren.",
    priceSeoTitle: "Prijzen",
    priceSeoDesc:
      "Transparante prijzen per kantoor voor Biedradar Pro. Pakketten vanaf €49/maand met 5 seats inbegrepen, 7 dagen gratis proberen en geen lock-in.",
    priceEyebrow: "Prijzen",
    priceTitle: "Eén werkplek voor je hele kantoor",
    priceSubtitle:
      "Elk pakket bevat 5 seats, de volledige Pro-app en 7 dagen gratis proberen. Schaal pas op als je groeit.",
    priceTrial: "7 dagen gratis proberen",
    priceNoCard: "7 dagen geen kosten",
    priceCancel: "Maandelijks opzegbaar",
    priceEu: "EU-gehost · AVG/GDPR",
    pricePerMonth: "/maand",
    priceSeatsIncluded: "5 seats inbegrepen",
    pricePerSeat: "daarna €5 / extra seat / maand",
    priceFeatured: "Meest gekozen",
    priceCta: "Start gratis proefperiode",
    priceStarterTagline: "Voor zzp-adviseurs & kleine teams",
    priceProTagline: "Voor groeiende kantoren",
    priceScaleTagline: "Voor kantoren met veel volume",
    priceRowActiveClients: "Actieve klanten",
    priceRowNewClients: "Nieuwe klanten / maand",
    priceRowUploads: "Documentuploads / maand",
    priceRowSigning: "Ondertekenverzoeken / maand",
    priceUnlimited: "Onbeperkt",
    priceSeatNote: "Alle pakketten bevatten 5 seats. Voeg teamleden toe voor €5 per seat per maand.",
    priceIncludedTitle: "In elk pakket",
    priceIncl1: "Realtime dealborden voor je hele team",
    priceIncl2: "Klantportaal in je huisstijl met document-upload",
    priceIncl3: "Rechtsgeldig ondertekenen (eIDAS)",
    priceIncl4: "Kadaster-, CBS- & PDOK-data in elke deal",
    priceIncl5: "EU-gehost, versleuteld, AVG-proof",
    priceIncl6: "Onbeperkt deals & woningen",
    priceGuaranteeTitle: "7 dagen gratis proberen, maandelijks opzegbaar",
    priceGuaranteeBody:
      "Probeer de volledige Pro-app 7 dagen gratis — zonder creditcard. Upgrade via facturatie wanneer jij er klaar voor bent; doe je dat niet, dan betaal je nooit iets. Geen lock-in, geen opzegkosten.",
    priceFaqTitle: "Vragen over prijzen?",
    priceFaqBody: "Lees de FAQ of mail ons — we helpen je het juiste pakket te kiezen.",
    priceFaqCta: "Lees de FAQ",
    priceClosingTitle: "Start vandaag je gratis proefperiode van 7 dagen",
    priceClosingBody: "Richt je kantoor in, nodig je team uit en draai je eerste deal binnen enkele minuten.",
    priceClosingCta: "Nu gratis starten",
    footerProduct: "Product",
    footerResources: "Bronnen",
    footerLegal: "Juridisch",
    linkFeatures: "Functies",
    linkPricing: "Prijzen",
    linkCalculator: "Calculator",
    linkContact: "Contact",
    linkGuides: "Gidsen",
    linkPrivacy: "Privacy",
    linkTerms: "Algemene voorwaarden",
    linkGdpr: "AVG",
    linkDisclaimer: "Disclaimer",
    linkEncryption: "Encryptie",
    linkDutchData: "Data in de EU",
    securityEyebrow: "Beveiliging & privacy",
    securityTitle: "De data van je klant: versleuteld en alleen in de EU",
    securityBody:
      "Biedradar Pro verwerkt gevoelige financiële en persoonsgegevens — end-to-end beveiligd en binnen de Europese Unie gehouden.",
    sec1Title: "Versleuteld onderweg én in rust",
    sec1Body:
      "Elke verbinding is TLS-versleuteld en data wordt versleuteld opgeslagen — van het eerste bod tot de getekende akte.",
    sec2Title: "EU-gehost, AVG-proof",
    sec2Body:
      "Servers en back-ups staan in Frankfurt; de data van je klanten verlaat de EER nooit. AVG by design.",
    sec3Title: "Rolgebaseerde toegang",
    sec3Body:
      "Fijnmazige teamrollen, en klanten zien alleen hun eigen deal — in een alleen-lezen portaal.",
    sec4Title: "Jij houdt de regie",
    sec4Body: "Exporteer of verwijder je data wanneer je wilt. Het is van jou, niet van ons.",
    securityPoliciesLabel: "Lees de details",
    momentEyebrow: "Het sleutelmoment",
    momentTitle: "Elke deal eindigt met iemand die verhuist.",
    momentBody:
      "Achter elk bod zit iemand die wacht op de sleutel. Biedradar Pro houdt de hele reis helder — voor jou, je team en je klant — zodat er niets tussen het eerste bod en de verhuisdag verloren gaat.",
    momentPhoto1Alt: "Een gezin verhuist naar hun nieuwe Nederlandse woning",
    momentPhoto2Alt: "Een koper bekijkt de hypotheek op een laptop",
    momentPhoto3Alt: "Een blij stel na de aankoop van hun woning",
    linkSecurity: "Beveiliging",
    linkCookies: "Cookies",
    baEyebrow: "Voor / Na",
    baTitle: "Van verspreide chaos naar één heldere werkruimte",
    baSubtitle:
      "Adviseurs die overstappen op Biedradar stoppen met jongleren tussen inbox en spreadsheets — en winnen meer deals, sneller.",
    baBeforeKicker: "Zonder Biedradar",
    baBeforeLabel: "Giswerk & geen overzicht",
    baAfterKicker: "Met Biedradar",
    baAfterLabel: "Elke deal onder controle",
    baBefore1: "Deals verspreid over inbox, WhatsApp en spreadsheets",
    baBefore2: "Biedingen gegokt zonder vergelijkbare verkopen of overbieddata",
    baBefore3: "Klanten wachten, deadlines verschuiven, waarde loopt weg",
    baAfter1: "Elke deal, klant en document in één werkruimte",
    baAfter2: "Onderbouwde biedingen — vergelijkbare verkopen, overbieddata, taxatierisico",
    baAfter3: "Meer klanten bediend, niets blijft liggen, deals sneller rond",
    baImgBeforeAlt: "Een onleesbare, versnipperde workflow vóór Biedradar",
    baImgAfterAlt: "Het Biedradar-dashboard: €2M+ pijplijn over tientallen actieve klanten",
    baAfterBadge1: "€3M+ pijplijn",
    baAfterBadge2: "27 actieve deals",
    baBeforeStat1V: "€800K",
    baBeforeStat1L: "vastgoed per maand",
    baBeforeStat2V: "1 op 3",
    baBeforeStat2L: "biedingen verloren",
    baBeforeStat3V: "10 u/wk",
    baBeforeStat3L: "kwijt aan losse tools",
    baAfterStat1V: "€2M+",
    baAfterStat1L: "vastgoed per maand",
    baAfterStat2V: "60%",
    baAfterStat2L: "vaker gewonnen",
    baAfterStat3V: "5×",
    baAfterStat3L: "meer klanten/maand",
    galleryEyebrow: "Van dichtbij",
    galleryTitle: "Gebouwd voor de diepte van een échte deal",
    gallerySubtitle: "Geen demo — de échte werkruimte waarin je team elke dag werkt.",
    galCap1: "Elke stap, met financiering en documenten",
    galCap2: "Spring overal heen met ⌘K",
    galCap3: "Elke woning over je deals",
    galCap4: "Je huisstijl, team en facturatie",
    footerTagline:
      "De deal-flow werkruimte voor Nederlandse hypotheekadviseurs en makelaars.",
    footerMadeInNl: "Gemaakt in NL",
    footerRights: "Alle rechten voorbehouden.",
  },
  legal: {
    backHome: "Terug naar Biedradar Pro",
    lastUpdated: "Laatst bijgewerkt",
    morePolicies: "Meer beleid",
  },
  topbar: {
    searchDeals: "Zoek deals…",
  },
  empty: {
    goToDeals: "Naar deals",
    dashTitle: "Welkom in je werkruimte",
    dashBody: "Maak je eerste deal aan en je ziet hier je pijplijn, financieringskloven en recente activiteit.",
    clientsTitle: "Nog geen deals",
    clientsBody: "Start je eerste deal — kies een pijplijn, voeg de klant en woning toe en ga aan de slag.",
    propertiesTitle: "Nog geen woningen",
    propertiesBody: "Woningen verschijnen hier zodra je woning- & bezichtigingsstappen aan je deals toevoegt.",
    sellersTitle: "Nog geen makelaars",
    sellersBody: "Verkopend makelaars die je op de woningstap van een deal toevoegt, zie je hier — over al je deals.",
    agendaTitle: "Niets gepland",
    agendaBody: "Bezichtigingen verschijnen hier zodra je een datum op de woningstap van een deal zet.",
    documentsTitle: "Nog geen documenten",
    documentsBody: "Vraag een bestand of handtekening op bij een deal, of upload er een — ze staan hier allemaal.",
    teamTitle: "Alleen jij",
    teamBody: "Nodig collega's uit om samen deals te doen. Beheerders beheren het team en de facturering.",
  },
  profile: { account: "Account", settings: "Instellingen" },
  viewings: {
    header: "Bezichtigingen",
    add: "Bezichtiging toevoegen",
    addPlaceholder: "Adres van een bezichtigde woning…",
    agent: "Verkopend makelaar",
    agentNone: "Geen makelaar",
    notes: "Notities",
    remove: "Bezichtiging verwijderen",
    status: { scheduled: "Gepland", viewed: "Bezichtigd", interested: "Interesse", rejected: "Afgevallen" },
  },
  viewing: {
    header: "Bezichtiging",
    viewedQuestion: "Bezichtigd?",
    yes: "Ja",
    no: "Nee",
    date: "Datum",
    statusViewed: "Bezichtigd",
    statusPlanned: "Gepland",
  },
  agents: {
    header: "Verkopend makelaars",
    buyer: "Koper",
    empty: "Nog geen verkopend makelaars.",
    primary: "Hoofd",
    makePrimary: "Instellen als hoofdmakelaar",
    remove: "Makelaar verwijderen",
    add: "Makelaar toevoegen",
    addTitle: "Verkopend makelaar toevoegen",
    adding: "Toevoegen…",
    addConfirm: "Makelaar toevoegen",
    name: "Contactpersoon",
    agency: "Kantoor",
    email: "E-mail",
    phone: "Telefoon",
  },
  clientCard: {
    open: "Klantkaart openen",
    title: "Klant",
    subtitle: "Contact, financiële positie & deze deal",
    name: "Naam",
    email: "E-mail",
    phone: "Telefoon",
    notes: "Notities",
    notesPlaceholder: "Alles wat belangrijk is om te onthouden over deze klant…",
    financialsSummary: "Financiële positie",
    editFinancials: "Financiën bewerken",
    income: "Bruto inkomen",
    ownFunds: "Eigen geld",
    gap: "Financieringskloof",
    dealSection: "Deze deal",
    save: "Opslaan",
    saving: "Opslaan…",
  },
  join: {
    title: "Word lid van de werkruimte",
    subtitle: "Accepteer je uitnodiging om samen aan deals te werken.",
    authSubtitle: "Maak een account aan of log in om de uitnodiging te accepteren — je komt meteen in de werkruimte.",
    confirmSubtitle: "Je bent ingelogd als {{email}}. Accepteer de uitnodiging om lid te worden.",
    nameLabel: "Je naam",
    emailLabel: "E-mail",
    passwordLabel: "Wachtwoord",
    emailHint: "Gebruik het e-mailadres waarnaar de uitnodiging is gestuurd.",
    createAndJoin: "Account aanmaken & meedoen",
    signInAndJoin: "Inloggen & meedoen",
    haveAccount: "Heb je al een account? Inloggen",
    noAccount: "Nieuw hier? Maak een account aan",
    accept: "Uitnodiging accepteren",
    joining: "Bezig…",
    emailMismatch: "Deze uitnodiging is voor een ander e-mailadres. Gebruik het uitgenodigde adres.",
    error: "Deze uitnodiging is ongeldig of verlopen.",
    toWorkspace: "Naar werkruimte",
  },
  public: {
    openApp: "App openen",
    footer: { home: "Home", status: "Status", contact: "Contact", privacy: "Privacy" },
  },
  contact: {
    eyebrow: "Contact",
    title: "Neem contact op",
    subtitle: "Vragen over Biedradar, je account of een samenwerking? Stuur ons een bericht en we reageren per e-mail.",
    nameLabel: "Naam",
    emailLabel: "E-mail",
    subjectLabel: "Onderwerp",
    subjectPlaceholder: "Waar gaat het over?",
    messageLabel: "Bericht",
    messagePlaceholder: "Waarmee kunnen we helpen?",
    send: "Bericht versturen",
    sending: "Versturen…",
    error: "Versturen mislukt — probeer het opnieuw, of mail ons rechtstreeks.",
    successTitle: "Bericht verstuurd",
    successBody: "Bedankt voor je bericht. We reageren zo snel mogelijk per e-mail.",
    privacyNote: "We gebruiken je gegevens alleen om te reageren.",
  },
  statusPage: {
    title: "Servicestatus",
    subtitle: "Live status van de systemen van Biedradar. Hiervoor is geen account nodig.",
    checking: "Controleren…",
    refresh: "Vernieuwen",
    updatedAt: "Bijgewerkt om {{time}}",
    footnote: "De status wordt elke 30 seconden automatisch ververst.",
    overall: {
      operational: "Alle systemen operationeel",
      degraded: "Sommige systemen verstoord",
      down: "Grote storing",
    },
    componentStatus: {
      operational: "Operationeel",
      degraded: "Verstoord",
      down: "Offline",
    },
    components: {
      api: "API",
      database: "Database",
      cache: "Cache",
      email: "E-mailbezorging",
    },
    componentsHint: {
      api: "Rapportgeneratie & app-backend",
      database: "Deals, klanten & documenten",
      cache: "Rate limiting & response-cache",
      email: "Uitnodigingen, ondertekenverzoeken & meldingen",
    },
  },
  customTemplate: {
    new: "Nieuw sjabloon",
    title: "Nieuw sjabloon",
    subtitle: "Bewaar een herbruikbare pijplijn. Nieuwe deals kunnen hiermee starten in plaats van met de standaard.",
    name: "Sjabloonnaam",
    namePlaceholder: "bijv. Nieuwbouw aankoop",
    steps: "Stappen",
    addStep: "Stap toevoegen",
    emptySteps: "Voeg minstens één stap toe.",
    save: "Sjabloon opslaan",
    saving: "Opslaan…",
    moveUp: "Omhoog",
    moveDown: "Omlaag",
    removeStep: "Stap verwijderen",
    delete: "Sjabloon verwijderen",
    stepCount_one: "{{count}} stap",
    stepCount_other: "{{count}} stappen",
  },
  newDeal: {
    title: "Nieuwe deal",
    subtitle: "Kies een pijplijn, voeg dan de klant en woning toe — de deal staat meteen klaar op het canvas.",
    sectionTemplate: "Pijplijn-sjabloon",
    sectionClient: "Klant",
    clientName: "Naam klant",
    clientEmail: "E-mail",
    clientPhone: "Telefoon",
    sectionProperty: "Woning",
    address: "Adres",
    addressPlaceholder: "Begin met typen…",
    postcode: "Postcode",
    askingPrice: "Vraagprijs",
    estimatedValue: "Geschatte waarde",
    sectionAssignment: "Toewijzing",
    assignee: "Toegewezen adviseur",
    unassigned: "Niet toegewezen",
    create: "Deal aanmaken",
    creating: "Aanmaken…",
    limitTitle: "Je hebt de klantlimiet van je abonnement bereikt.",
    limitBody: "Upgrade om deze maand meer klanten toe te voegen.",
    viewPlans: "Bekijk abonnementen",
    error: "Kon de deal niet aanmaken. Probeer het opnieuw.",
  },
  templates: {
    buy: { name: "Aankoop", description: "De standaard kopersreis: van bezichtiging tot sleuteloverdracht." },
    mortgage: { name: "Hypotheekadvies", description: "Adviestraject: intake, aanvraag, taxatie en passeren." },
    sell: { name: "Verkoop", description: "Verkooptraject: van opname tot overdracht bij de notaris." },
    refinance: { name: "Oversluiten", description: "Bestaande hypotheek oversluiten naar betere voorwaarden." },
  },
  dealSettings: {
    title: "Deal-instellingen",
    name: "Dealnaam",
    description: "Omschrijving",
    descriptionPlaceholder: "Notities over deze deal…",
    address: "Adres",
    addressPlaceholder: "Straat, nummer, plaats",
    status: "Status",
  },
  stepInfo: {
    awaitingOffer: "Wacht op bod",
    transferAtNotary: "Overdracht bij notaris",
    eSignature: "Digitale handtekening",
    signed: "Ondertekend",
    signatureCount_one: "{{count}} te ondertekenen",
    signatureCount_other: "{{count}} te ondertekenen",
    documentsCount_one: "{{count}} document",
    documentsCount_other: "{{count}} documenten",
    documentsRequested: "Documenten opgevraagd",
    financialProfile: "Financiële positie",
  },
  deal: {
    backToClients: "Terug naar klanten",
    loading: "Deal laden…",
    notFound: "Deze deal kon niet worden gevonden.",
    shareSpace: "Space delen",
    copySpaceLink: "Space-link kopiëren",
    linkCopied: "Link gekopieerd",
    creating: "Aanmaken…",
    fullyFinanced: "Volledig gefinancierd",
    viewing: "{{count}} kijkt mee: {{names}}",
    advisorFallback: "Adviseur",
    commentsButton: "Overleg",
    commentsTitle: "Deal-overleg",
    commentsEmpty: "Nog geen opmerkingen. Start het overleg over deze deal.",
  },
  financing: {
    openButton: "Financiën",
    title: "Financiering",
    subtitle: "De positie van {{client}} en de hypotheek voor deze deal.",
    clientSection: "Klant — inkomen & eigen geld",
    grossIncome: "Bruto jaarinkomen",
    partnerIncome: "Bruto inkomen partner",
    ownFunds: "Eigen geld (spaargeld)",
    otherMonthlyIncome: "Overig inkomen (per maand)",
    monthlyObligations: "Maandelijkse verplichtingen",
    studentDebt: "Studieschuld",
    mortgageSection: "Hypotheek",
    bank: "Bank",
    bankPlaceholder: "bijv. ABN AMRO, Rabobank",
    maxMortgage: "Maximale hypotheek",
    interestRate: "Rente (%)",
    termYears: "Looptijd (jaren)",
    mortgageType: "Aflosvorm",
    nhg: "NHG (hypotheekgarantie)",
    type: { annuity: "Annuïteit", linear: "Lineair", interest_only: "Aflossingsvrij" },
    gapSection: "Financieringskloof",
    purchasePrice: "Koopprijs",
    appliedMax: "Hypotheek (max)",
    ownFundsApplied: "Eigen geld",
    gap: "Eigen-geldkloof",
    fullyFinanced: "Volledig gefinancierd",
    save: "Financiering opslaan",
    saving: "Opslaan…",
  },
  settings: {
    title: "Instellingen",
    subtitle: "Beheer je organisatie, team en account.",
    orgTitle: "Organisatie-instellingen",
    orgSubtitle: "De identiteit, huisstijl en het team van je werkruimte.",
    nav: { organization: "Organisatie", members: "Leden", billing: "Facturering", account: "Account" },
    org: {
      title: "Organisatie",
      nameLabel: "Organisatienaam",
      plan: "Abonnement",
      accent: "Accentkleur",
      accentHint: "Gebruikt in het klantportaal en gedeelde rapporten.",
      accentInvalid: "Gebruik een hex-kleur zoals #0f7652.",
      save: "Wijzigingen opslaan",
      saving: "Opslaan…",
      saved: "Opgeslagen",
      saveFailed: "Opslaan mislukt — controleer de velden en probeer opnieuw.",
      memberCount_one: "{{count}} lid in deze werkruimte",
      memberCount_other: "{{count}} leden in deze werkruimte",
      brandingTitle: "Huisstijl",
      brandingSubtitle: "Zichtbaar in het klantportaal en gedeelde rapporten.",
      logoLabel: "Logo-URL",
      logoHint: "Directe link naar een PNG of SVG. Laat leeg voor het Biedradar-woordmerk.",
      logoInvalid: "Vul een geldige http(s)-URL in, of laat het veld leeg.",
      logoPreviewAlt: "Organisatielogo",
      teamTitle: "Team",
      teamSubtitle: "Wie er in deze werkruimte zit, per rol.",
      roleAdmins: "Beheerders",
      roleAdvisors: "Adviseurs",
      roleAssistants: "Assistenten",
      roleClients: "Portaalklanten",
      manageTeam: "Leden beheren",
      businessTitle: "Bedrijfsgegevens",
      businessSubtitle: "Gebruikt op facturen, gedeelde rapporten en in het klantportaal.",
      portalTitle: "Klantportaal",
      portalSubtitle: "Wat klanten zien als ze hun dealomgeving openen.",
      profile: {
        contactEmail: "Contact-e-mail",
        phone: "Telefoon",
        website: "Website",
        kvk: "KvK-nummer",
        vat: "Btw-nummer",
        addressLine: "Adres",
        postalCode: "Postcode",
        city: "Plaats",
        supportEmail: "Support-e-mail",
        portalWelcome: "Welkomstbericht portaal",
        portalWelcomePlaceholder: "Een korte begroeting die klanten in hun omgeving zien…",
      },
      detailsTitle: "Gegevens",
      orgId: "Organisatie-ID",
      copyId: "ID kopiëren",
      created: "Aangemaakt",
      dangerTitle: "Gevarenzone",
      dangerBody: "Een organisatie verwijderen is definitief — alle deals, documenten en leden verdwijnen. Neem contact op, dan regelen we het samen.",
      dangerContact: "Contact opnemen",
    },
    members: {
      title: "Leden & rollen",
      subtitle: "Beheerders nodigen het team uit en beheren het. Leden werken aan deals; alleen-lezen klanten zien het portaal.",
      you: "jij",
      remove: "Lid verwijderen",
      removeConfirm: "{{name}} uit deze werkruimte verwijderen?",
      rolesHint: "Beheerder — beheren & uitnodigen · Lid — deals · Klant — alleen-lezen.",
      inviteTitle: "Teamlid uitnodigen",
      invitePlaceholder: "collega@kantoor.nl",
      inviteSend: "Uitnodiging sturen",
      inviteSending: "Versturen…",
      inviteLinkLabel: "Uitnodigingslink (ook gemaild) — deel deze om mee te doen:",
      copied: "Gekopieerd!",
      pendingTitle: "Openstaande uitnodigingen",
      pendingEmpty: "Geen openstaande uitnodigingen.",
      revoke: "Uitnodiging intrekken",
    },
    account: {
      title: "Account",
      subtitle: "Je persoonlijke profiel, voorkeuren en beveiliging.",
      profileTitle: "Profiel",
      preferencesTitle: "Voorkeuren",
      language: "Taal",
      theme: "Thema",
      securityTitle: "Beveiliging",
      demoNote: "Je bekijkt de demo-werkruimte. Log in om je eigen account te beheren.",
      passwordTitle: "Wachtwoord wijzigen",
      currentPassword: "Huidig wachtwoord",
      newPassword: "Nieuw wachtwoord",
      passwordMin: "Minimaal 8 tekens.",
      passwordSave: "Wachtwoord bijwerken",
      passwordSaving: "Bijwerken…",
      passwordSaved: "Wachtwoord bijgewerkt. Andere sessies zijn uitgelogd.",
      passwordError: "Wachtwoord bijwerken mislukt — controleer je huidige wachtwoord.",
    },
  },
  teamErrors: {
    already_member: "Dit adres hoort al bij een teamlid.",
    last_admin: "Een organisatie heeft minstens één beheerder nodig — maak eerst iemand anders beheerder.",
    cannot_remove_self: "Je kunt je eigen lidmaatschap niet verwijderen.",
    rate_limited: "Te veel pogingen — probeer het straks opnieuw.",
    forbidden: "Alleen beheerders kunnen het team beheren.",
    generic: "Dat is niet gelukt — probeer het opnieuw.",
  },
  dashboard: {
    title: "Overzicht",
    subtitlePipeline: "{{name}} — pijplijn in één oogopslag",
    yourWorkspace: "Je werkruimte",
    activeDeals: "Actieve deals",
    won: "Gewonnen",
    needAction: "Actie nodig",
    pipelineValue: "Pijplijnwaarde",
    ownFundsGap: "{{amount}} eigen-geldkloof",
    fullyFinanced: "volledig gefinancierd",
    needsAttention: "Vereist aandacht",
    recentActivity: "Recente activiteit",
    nothingBlocked: "Niets geblokkeerd. Alles loopt soepel.",
    noDeals: "Nog geen deals.",
    blocked: "{{count}} geblokkeerd",
  },
  clients: {
    title: "Klanten",
    subtitle_one: "{{count}} deal in je pijplijn",
    subtitle_other: "{{count}} deals in je pijplijn",
    filterAll: "Alle",
    filterActive: "Actief",
    filterWon: "Gewonnen",
    filterOnHold: "Gepauzeerd",
    searchPlaceholder: "Zoeken…",
    noMatch: "Geen deals met deze filters.",
    filterFinancing: "Financiering",
    finAll: "Alle financiering",
    finGap: "Eigen-geldkloof",
    finFinanced: "Volledig gefinancierd",
    sort: "Sorteer",
    sortRecent: "Recente activiteit",
    sortName: "Klantnaam",
    sortValue: "Pijplijnwaarde",
    sortGap: "Eigen-geldkloof",
    colDeal: "Deal",
    colStatus: "Status",
    colProgress: "Voortgang",
    colFinancing: "Financiering",
    colNextAction: "Volgende actie",
    colActivity: "Activiteit",
    blocked: "{{count}} geblokkeerd",
    gap: "{{amount}} kloof",
    funded: "Gefinancierd",
    rowMenu: "Deal-acties",
    openDeal: "Deal openen",
    setStatus: "Status instellen",
    deleteDeal: "Deal verwijderen",
    deleteConfirm: "Deal van {{name}} verwijderen? Dit verwijdert het canvas, documenten en opmerkingen.",
    emptyTitle: "Hier nog geen deals",
    emptyBody:
      "Maak je eerste deal aan en de standaard kopersreis verschijnt op het canvas, klaar om te starten.",
  },
  login: {
    createTitle: "Maak je account aan",
    signInTitle: "Inloggen",
    createSubtitle: "Stel je adviseursaccount in om te beginnen.",
    signInSubtitle: "Welkom terug. Ga verder waar je was gebleven.",
    yourName: "Je naam",
    workEmail: "Werk-e-mail",
    emailPlaceholder: "jij@kantoor.nl",
    password: "Wachtwoord",
    createAccount: "Account aanmaken",
    continue: "Doorgaan",
    alreadyHaveAccount: "Heb je al een account?",
    noAccountYet: "Nog geen account?",
    createOne: "Maak er een aan",
    errCreate: "Kon je account niet aanmaken. Probeer het opnieuw.",
    errSignin: "Inloggen mislukt. Controleer je e-mail en wachtwoord.",
    errExists: "Er bestaat al een account met dit e-mailadres. Log in plaats daarvan in.",
    errPasswordLength: "Het wachtwoord moet minimaal 8 tekens lang zijn.",
    forgotPassword: "Wachtwoord vergeten?",
    forgotTitle: "Wachtwoord opnieuw instellen",
    forgotSubtitle: "Vul je e-mailadres in en we sturen je een herstellink.",
    forgotSubmit: "Herstellink sturen",
    forgotSent: "Als er een account bestaat voor dit adres, is er een herstellink onderweg. Check je inbox.",
    backToSignIn: "Terug naar inloggen",
    resetTitle: "Kies een nieuw wachtwoord",
    resetSubtitle: "Bijna klaar — stel een nieuw wachtwoord in voor je account.",
    resetNewPassword: "Nieuw wachtwoord",
    resetSubmit: "Nieuw wachtwoord instellen",
    resetSuccess: "Wachtwoord bijgewerkt. We sturen je door naar inloggen…",
    resetInvalidToken: "Deze herstellink is ongeldig of verlopen. Vraag een nieuwe aan.",
  },
  verifyEmail: {
    title: "Bevestig je e-mail",
    body: "We hebben een bevestigingslink gestuurd naar",
    checkInbox:
      "Open de e-mail en klik op de link om je account te activeren. Geen e-mail? Kijk ook in je spam.",
    resend: "E-mail opnieuw sturen",
    changeEmail: "Ander e-mailadres gebruiken",
    resendCooldown: "Verstuurd — controleer je inbox.",
    resendError: "Kon de e-mail niet opnieuw sturen. Probeer het zo nog eens.",
  },
  verified: {
    successTitle: "E-mail bevestigd",
    successBody: "Je e-mail is bevestigd. Je bent klaar om te beginnen.",
    failedTitle: "Bevestiging mislukt",
    failedBody: "Deze link is ongeldig of verlopen. Vraag een nieuwe aan.",
    ctaWorkspace: "Open de werkruimte",
    ctaSignin: "Inloggen",
    ctaResend: "Bevestigingsmail opnieuw sturen",
  },
  onboarding: {
    title: "Maak je werkruimte aan",
    subtitle: "Geef je teamwerkruimte een naam. Je kunt dit later in Instellingen wijzigen.",
    nameLabel: "Naam werkruimte",
    nameHint: "Laat leeg om je eigen naam te gebruiken.",
    error: "Kon de werkruimte niet aanmaken. Probeer het opnieuw.",
    creating: "Aanmaken…",
    create: "Werkruimte aanmaken",
  },
  start: {
    // chrome
    stepOf: "Stap {{current}} van {{total}}",
    back: "Terug",
    continue: "Doorgaan",
    skip: "Sla over",
    finishing: "Bezig met instellen…",
    haveAccount: "Heb je al een account?",
    noAccount: "Nog geen account?",
    signInLink: "Inloggen",
    createLink: "Maak er een aan",
    // plan
    planTitle: "Kies je abonnement",
    planSubtitle: "Begin met 7 dagen gratis proberen. Geen creditcard nodig — maandelijks opzegbaar.",
    planPopular: "Populairst",
    planMonth: "/mnd",
    planPerSeat: "per gebruiker · maandelijks gefactureerd",
    planContinue: "Ga verder met {{plan}}",
    planTrialNote: "Geen creditcard nodig — tijdens je proefperiode van 7 dagen betaal je niets.",
    // account
    accountTitle: "Maak je account aan",
    accountSubtitle: "Dit wordt je persoonlijke login voor Biedradar Pro.",
    accountSigninTitle: "Inloggen",
    accountSigninSubtitle: "Welkom terug — laten we het afmaken.",
    name: "Je naam",
    email: "Werk-e-mail",
    emailPlaceholder: "jij@kantoor.nl",
    password: "Wachtwoord",
    creatingAccount: "Account aanmaken…",
    errAccount: "Kon je account niet aanmaken. Probeer het opnieuw.",
    errExists: "Met dit e-mailadres bestaat al een account — log in om verder te gaan.",
    errSignin: "Inloggen mislukt. Controleer je e-mail en wachtwoord.",
    // workspace
    workspaceTitle: "Richt je werkruimte in",
    workspaceSubtitle: "Wat context zodat we Biedradar op jouw team afstemmen.",
    workspaceName: "Naam werkruimte",
    workspaceNameHint: "Meestal de naam van je kantoor of bedrijf.",
    workspaceNamePlaceholder: "bijv. Hendriks Hypotheken",
    teamSize: "Hoe groot is je team?",
    teamSize_solo: "Alleen ik",
    teamSize_small: "2–5",
    teamSize_medium: "6–20",
    teamSize_large: "20+",
    role: "Wat is jouw rol?",
    role_advisor: "Hypotheekadviseur",
    role_agent: "Makelaar",
    role_manager: "Teamlead / eigenaar",
    role_other: "Iets anders",
    errWorkspace: "Kon de werkruimte niet aanmaken. Probeer het opnieuw.",
    // invite
    inviteTitle: "Nodig je team uit",
    inviteSubtitle: "Voeg nu collega's toe, of sla over en nodig ze later uit via Instellingen.",
    inviteEmail: "collega@kantoor.nl",
    inviteRoleLabel: "Rol",
    inviteRole_admin: "Beheerder",
    inviteRole_advisor: "Adviseur",
    inviteRole_assistant: "Assistent",
    inviteAdd: "Nog een toevoegen",
    inviteSend: "Uitnodigingen versturen",
    inviteSending: "Uitnodigingen versturen…",
    errInvite: "Sommige uitnodigingen konden niet worden verstuurd. Controleer de adressen en probeer opnieuw.",
    // done
    doneTitle: "Helemaal klaar",
    doneSubtitle: "Je werkruimte staat klaar — je gratis proefperiode is al gestart.",
    doneVerifyNote:
      "Nog één ding: we hebben een bevestigingslink gestuurd naar {{email}}. Bevestig je e-mail om je werkruimte te openen — kijk ook in je spam.",
    doneTrialNote: "Je 7 dagen gratis proefperiode van het {{plan}}-abonnement is gestart — geen creditcard nodig. Upgraden kan altijd via Instellingen → Facturering.",
    doneTrialCta: "Start {{plan}}-proefperiode",
    redirecting: "Doorsturen naar afrekenen…",
    errTrial: "Kon de proefperiode nu niet starten — je werkruimte werkt gewoon; probeer het later via Instellingen → Facturering.",
    doneCta: "Open je werkruimte",
  },
  billing: {
    title: "Facturering",
    loading: "Laden…",
    trialLeft_one: "Gratis proefperiode — nog {{count}} dag · zeg vóór die tijd op en je betaalt niets",
    trialLeft_other: "Gratis proefperiode — nog {{count}} dagen · zeg vóór die tijd op en je betaalt niets",
    startTrial: "Start 7-daagse gratis proefperiode",
    starting: "Starten…",
    seatsUsed: "Stoelen in gebruik",
    seatsOf: "{{used}} van {{included}} inbegrepen",
    extraSeats_one: "{{count}} extra stoel · +{{amount}}/mnd",
    extraSeats_other: "{{count}} extra stoelen · +{{amount}}/mnd",
    usageTitle: "Verbruik deze maand",
    usageActiveClients: "Actieve klanten",
    usageNewClients: "Nieuwe klanten",
    usageUploads: "Documentuploads",
    usageSigning: "Ondertekenverzoeken",
    planLine: "Plan ({{seats}} stoelen incl.)",
    seatOverage: "Stoel-toeslag",
    total: "Totaal",
    plansTitle: "Abonnementen",
    noOnlinePayment: "Het online afrekenen kon zojuist niet starten — probeer het zo nog eens.",
    billedNote: "7 dagen gratis proberen, geen creditcard nodig — pas na je upgrade maandelijks gefactureerd via Mollie. 5 stoelen inbegrepen per plan, daarna €5/stoel. Maandelijks opzegbaar.",
    perMonth: "/mnd",
    noPlan: "Geen abonnement",
    planSuffix: "{{name}}-abonnement",
    proPlan: "Pro-abonnement",
    current: "Huidig",
    currentPlan: "Huidig abonnement",
    redirecting: "Doorsturen…",
    subscribe: "Abonneren",
    unlimited: "Onbeperkt",
    status: { none: "Geen abonnement", trialing: "Proef", active: "Actief", past_due: "Te laat", canceled: "Geannuleerd" },
    rowActiveClients: "Actieve klanten",
    rowNewClients: "Nieuwe klanten / mnd",
    rowUploads: "Uploads / mnd",
    rowSigning: "Ondertekenen / mnd",
  },
  status: {
    deal: { active: "Actief", won: "Gewonnen", lost: "Verloren", on_hold: "Gepauzeerd" },
    step: {
      not_started: "Niet gestart",
      in_progress: "Bezig",
      blocked: "Geblokkeerd",
      done: "Klaar",
    },
  },
  roles: {
    admin: "Beheerder",
    advisor: "Adviseur",
    assistant: "Assistent",
    client: "Klant",
  },
  team: {
    title: "Team",
    seatsUsed: "{{used}} van {{total}} inbegrepen stoelen in gebruik",
    invite: "Lid uitnodigen",
    inviteCancel: "Annuleren",
    role: "Rol",
    you: "jij",
    remove: "Lid verwijderen",
    removeConfirm: "{{name}} uit deze werkruimte verwijderen?",
    pendingTitle: "Openstaande uitnodigingen",
    revoke: "Intrekken",
    seatsFootnote: "5 stoelen inbegrepen in elk abonnement · elke extra stoel wordt maandelijks gefactureerd.",
    emailPlaceholder: "collega@makelaardij.nl",
    send: "Uitnodiging versturen",
    sending: "Versturen…",
    inviteLinkLabel: "Uitnodigingslink (ook gemaild) — deel hem om mee te doen:",
    copied: "Gekopieerd!",
  },
  stepType: {
    property: "Woning & bezichtiging",
    report: "Biedradar-rapport",
    offer: "Bod",
    offer_accepted: "Bod geaccepteerd",
    appraisal: "Taxatie",
    mortgage: "Hypotheek / bank",
    notary: "Notaris / passeren",
    sign: "Document tekenen",
    document_request: "Documentverzoek",
    financials: "Financiën",
    custom: "Aangepaste stap",
  },
  nextAction: {
    won: "Bij de notaris gepasseerd",
    ready: "Klaar om af te ronden",
    blocked: "Geblokkeerd · {{step}}",
  },
  time: { justNow: "zojuist" },
  portal: {
    loading: "Laden…",
    openingSpace: "Je space wordt geopend…",
    inviteInvalid: "Deze uitnodigingslink is ongeldig of verlopen.",
    loadingSpace: "Je space laden…",
    spaceUnavailable: "Deze space is niet beschikbaar, of je hebt er geen toegang toe.",
    loadingSpaces: "Je spaces laden…",
    readOnlyBadge: "Alleen-lezen klantomgeving",
    journey: "Jouw aankooptraject",
    stepsProgress_one: "{{done}} van {{total}} stap",
    stepsProgress_other: "{{done}} van {{total}} stappen",
    documents: "Documenten",
    documentsEmpty: "Nog geen documenten.",
    uploadDocument: "Document uploaden",
    uploading: "Uploaden…",
    requestedByAdvisor: "Opgevraagd door je adviseur",
    received: "Ontvangen",
    upload: "Uploaden",
    messages: "Berichten",
    messagesEmpty: "Nog geen berichten.",
    messagePlaceholder: "Schrijf een bericht…",
    send: "Versturen",
    sending: "Versturen…",
    toSign: "Te ondertekenen",
    signed: "Ondertekend",
    awaitingSignature: "Wacht op ondertekening — check je e-mail",
    home: {
      title: "Jouw spaces",
      signedInAs: "Ingelogd als {{name}}. ",
      pickPurchase: "Kies een aankoop om te bekijken.",
      emptyBody: "Je hebt nog geen spaces. Zodra je adviseur er een met je deelt, verschijnt die hier.",
      emptyTip: "Tip: open de uitnodigingslink die je adviseur je heeft gemaild om te beginnen.",
    },
    auth: {
      heading: "Jouw woningdossier",
      inviteSubheading: "Log in of maak een account aan om de space te openen die je adviseur met je heeft gedeeld.",
      subheadingRegister: "Maak een account aan om je aankoop te volgen, documenten te delen en je adviseur te berichten.",
      subheadingSignIn: "Log in om je aankoop te volgen, documenten te delen en je adviseur te berichten.",
      nameLabel: "Je naam",
      namePlaceholder: "Anna Bakker",
      emailLabel: "E-mail",
      emailPlaceholder: "jij@email.nl",
      passwordLabel: "Wachtwoord",
      createAccount: "Account aanmaken",
      continue: "Doorgaan",
      alreadyHaveAccount: "Heb je al een account?",
      firstTimeHere: "Voor het eerst hier?",
      signIn: "Inloggen",
      createOne: "Maak er een aan",
      errCreate: "Kon je account niet aanmaken. Probeer het opnieuw.",
      errSignIn: "Inloggen mislukt. Controleer je e-mail en wachtwoord.",
    },
  },
  lang: { label: "Taal", nl: "Nederlands", en: "Engels" },
  stepTypeDesc: {
    property: "De doelwoning en bezichtigingen",
    report: "Haal het daterapport op in de deal",
    offer: "Volg het bod en de voorbehouden",
    offer_accepted: "Mijlpaal: bod geaccepteerd",
    appraisal: "NWWI-taxatie en waardebepaling",
    mortgage: "Mijlpalen hypotheekaanvraag",
    notary: "Passeren bij de notaris",
    sign: "Stuur een document ter ondertekening",
    document_request: "Vraag bestanden op bij de klant",
    financials: "Betaalbaarheidsoverzicht en financieringskloof",
    custom: "Alles wat je zelf definieert",
  },
  canvas: {
    addStep: "Stap toevoegen",
    internal: "intern",
    internalHint: "Intern — verborgen voor het klantportaal",
    sellingAgent: "Verkopend makelaar",
    deleteConnection: "Verbinding verwijderen",
    stepPanel: {
      close: "Sluiten",
      tabDetails: "Details",
      tabNotes: "Notities",
      tabFiles: "Documenten",
      tabComments: "Opmerkingen",
      notesHint: "Uitgebreide notities — zichtbaar voor je team (niet in het klantportaal).",
      locationHeader: "Locatie",
      locationNoCoords: "Nog geen coördinaten — voeg het adres via autocomplete toe om het op de kaart te zetten.",
      openInMaps: "Open in kaart",
      bidConditions: "Voorbehouden",
      bidConditionsPlaceholder: "Financiering, bouwkundige keuring…",
      labelTitle: "Titel",
      labelStatus: "Status",
      labelNotes: "Notities",
      labelVisibility: "Zichtbaarheid klantportaal",
      notesPlaceholder: "Voeg een interne notitie toe…",
      visibilityShared: "gedeeld",
      visibilityInternal: "intern",
      visibilitySharedHint: "Zichtbaar voor de klant in het leesportaal.",
      visibilityInternalHint: "Verborgen voor het klantportaal.",
      markComplete: "Markeer als voltooid",
      deleteStep: "Stap verwijderen",
      noFiles: "Nog geen documenten bij deze stap.",
      uploadFile: "Document uploaden",
      uploading: "Uploaden…",
      requestFilePlaceholder: "Vraag een document op bij de klant…",
      requestFile: "Opvragen",
      noComments: "Nog geen opmerkingen. Start de discussie.",
      commentPlaceholder: "Schrijf een opmerking…",
      comment: "Plaatsen",
      posting: "Plaatsen…",
      clientBadge: "klant",
      requestedFrom: "Opgevraagd bij klant",
      download: "Downloaden",
      upload: "Uploaden",
      markInReview: "Markeer als in beoordeling",
      approve: "Goedkeuren",
      docRequested: "Opgevraagd",
      docReceived: "Ontvangen",
      docInReview: "In beoordeling",
      docApproved: "Goedgekeurd",
      signatureHeader: "Handtekening",
      signDocTitlePlaceholder: "Documenttitel",
      signSignerPlaceholder: "Naam ondertekenaar",
      signNoDoc: "Geen document — alleen akkoord",
      createSigningLink: "Ondertekeningslink aanmaken",
      creatingSigningLink: "Aanmaken…",
      copied: "Gekopieerd!",
      financingHeader: "Financiering",
      financingAskingPrice: "Vraagprijs",
      financingLatestBid: "Laatste bod",
      financingEstimatedValue: "Geschatte waarde",
      financingMaxMortgage: "Maximale hypotheek",
      financingOwnFundsGap: "Eigen-geldkloof",
      financingFullyFinanced: "Volledig gefinancierd",
      reportHeader: "Biedradar-rapport",
      reportBody:
        "Haal de Kadaster-taxatie, vergelijkbare woningen en waarderingsrisico op voor {{address}} in de deal.",
      pullReport: "Rapport ophalen",
      agentLink: "Verkopend makelaar",
      agentLinkNone: "Geen makelaar gekoppeld",
      agentAddNew: "Nieuwe makelaar",
      agentAddConfirm: "Makelaar toevoegen",
      agentLinkAddFirst: "Voeg eerst een verkopend makelaar toe via het paneel op het canvas en koppel hem hier.",
      stepAddressPlaceholder: "Stel het adres van deze woning in…",
      stepAddressHint: "Elke woningstap kan een eigen adres hebben — los van het hoofddoel van de deal.",
    },
  },
};

void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: en },
      nl: { translation: nl },
    },
    fallbackLng: "nl",
    supportedLngs: SUPPORTED_LANGS as unknown as string[],
    nonExplicitSupportedLngs: true, // treat "en-US" etc. as "en"
    interpolation: { escapeValue: false }, // React already escapes
    detection: {
      order: ["localStorage", "navigator"],
      lookupLocalStorage: LANG_STORAGE_KEY,
      caches: ["localStorage"],
    },
    react: { useSuspense: false },
  });

export default i18n;
